<template>
  <Layout>
    <div class="dygaoyong-page">
      <div class="page-content">
        <el-tabs v-model="libraryTab" class="library-tabs" @tab-click="handleLibraryTabChange">
          <el-tab-pane label="选品库" name="selection"></el-tab-pane>
          <el-tab-pane label="我的收藏" name="favorites"></el-tab-pane>
        </el-tabs>

        <!-- 页面头部 -->
        <div class="page-header">
          <div class="header-search">
            <div class="search-box" ref="searchBox">
              <div class="search-row">
                <el-select
                  v-model="searchType"
                  class="search-type-select"
                  :disabled="libraryTab === 'favorites'"
                  @change="handleSearchTypeChange"
                >
                  <el-option
                    v-for="option in searchTypeOptions"
                    :key="option.value"
                    :label="option.label"
                    :value="option.value"
                  />
                </el-select>
                <el-input
                  ref="searchInput"
                  v-model="searchKeyword"
                  :placeholder="searchPlaceholder"
                  class="search-input-large"
                  clearable
                  @keyup.enter.native="handleSearch"
                  @clear="handleSearchClear"
                  @focus="handleSearchFocus"
                  @input="handleSearchInput"
                  @blur="handleSearchBlur"
                />
                <el-button class="search-button" type="primary" icon="el-icon-search" @click="handleSearch">搜索</el-button>
              </div>
              <div class="suggest-box" v-if="searchPanelMode === 'suggest' && suggestList && suggestList.length">
                <ul>
                  <li
                    v-for="(item, index) in suggestList"
                    :key="`${item.value}-${index}`"
                    class="suggest-item"
                    @click="fastSearch(item.value)"
                    @mousedown.prevent
                  >
                    <i class="el-icon-search"></i> {{ item.value }}
                  </li>
                </ul>
              </div>
              <div class="history-box" v-if="searchPanelMode === 'history' && searchHistory && searchHistory.length">
                <div class="history-header">
                  <span>搜索历史</span>
                  <span class="history-clear" @click="clearHistory" @mousedown.prevent>清空</span>
                </div>
                <ul>
                  <li
                    v-for="(item, index) in searchHistory"
                    :key="`${item.type}-${item.keyword}-${index}`"
                    class="history-item"
                    @click="fastSearch(item.keyword, item.type)"
                    @mousedown.prevent
                  >
                    <span class="history-item-main">
                      <i class="el-icon-time"></i>
                      <span class="history-keyword">{{ item.keyword }}</span>
                    </span>
                    <el-tag size="mini" effect="plain" :type="getSearchTypeTagType(item.type)">
                      {{ getSearchTypeLabel(item.type) }}
                    </el-tag>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        <!-- 筛选区域 -->
        <el-card v-if="libraryTab === 'selection'" class="card-container" shadow="never">
          <div class="filter-section">
            <!-- 类目筛选 -->
            <div class="filter-group">
              <!-- <label class="filter-label">商品类目：</label> -->
              <el-radio-group v-model="categoryId" @change="handleCategoryChange">
                <el-radio-button v-for="category in categoryList" :key="category.cat_id" :label="category.cat_id">{{
                  category.cat_name
                }}</el-radio-button>
              </el-radio-group>
            </div>

            <div v-if="brandList.length" class="filter-group brand-filter-group">
              <!-- <label class="filter-label">品牌：</label> -->
              <div class="brand-filter-content">
                <div
                  ref="brandList"
                  class="brand-list"
                  :class="{ 'brand-list--collapsed': showBrandToggle && !brandsExpanded }"
                >
                  <button
                    type="button"
                    class="brand-item brand-item--all"
                    :class="{ active: !brandName }"
                    @click="handleBrandChange('')"
                  >
                    全部品牌
                  </button>
                  <button
                    v-for="brand in inlineBrandList"
                    :key="brand.brand_name"
                    type="button"
                    class="brand-item"
                    :class="{ active: brandName === brand.brand_name }"
                    :title="brand.brand_name"
                    @click="handleBrandChange(brand.brand_name)"
                  >
                    <el-tooltip
                      :content="isFavoriteBrand(brand.brand_name) ? '取消收藏' : '收藏品牌'"
                      placement="top"
                    >
                      <span
                        class="brand-favorite"
                        :class="{ active: isFavoriteBrand(brand.brand_name) }"
                        role="button"
                        tabindex="0"
                        :aria-label="isFavoriteBrand(brand.brand_name) ? '取消收藏' : '收藏品牌'"
                        @click.stop="toggleFavoriteBrand(brand.brand_name)"
                        @keydown.enter.stop.prevent="toggleFavoriteBrand(brand.brand_name)"
                        @keydown.space.stop.prevent="toggleFavoriteBrand(brand.brand_name)"
                      >
                        <i :class="isFavoriteBrand(brand.brand_name) ? 'el-icon-star-on' : 'el-icon-star-off'"></i>
                      </span>
                    </el-tooltip>
                    <el-image class="brand-logo" :src="brand.logo" fit="contain" lazy>
                      <div slot="error" class="brand-logo-error">
                        <i class="el-icon-picture-outline"></i>
                      </div>
                    </el-image>
                    <span class="brand-name">{{ brand.brand_name }}</span>
                  </button>
                </div>
                <div v-if="showBrandToggle" class="brand-toggle-row">
                  <el-button type="text" @click="brandsExpanded = !brandsExpanded">
                    {{ brandsExpanded ? "收起品牌" : "展开全部品牌" }}
                    <i :class="brandsExpanded ? 'el-icon-arrow-up' : 'el-icon-arrow-down'"></i>
                  </el-button>
                </div>
                <button
                  v-if="isMobile && brandList.length > mobileInlineBrandLimit"
                  type="button"
                  class="mobile-brand-more"
                  @click="openMobileBrandDrawer"
                >
                  <span>查看全部 {{ brandList.length }} 个品牌</span>
                  <i class="el-icon-arrow-up"></i>
                </button>
              </div>
            </div>

            <div class="filter-group">
              <!-- <label class="filter-label">直播状态：</label> -->
              <el-switch v-model="onlyLive" active-text="仅看直播中" @change="handleLiveStatusChange" />
            </div>

            <!-- 排序选择 -->
            <div class="filter-group">
              <!-- <label class="filter-label">排序方式：</label> -->
              <div class="sort-buttons">
                <div
                  class="sort-item"
                  :class="{ active: sortValue === 0 }"
                  @click="handleSortClick('comprehensive')"
                >
                  <span class="sort-label">综合</span>
                </div>
                <div
                  class="sort-item"
                  :class="{ active: isSortFieldActive('sales_num') }"
                  @click="handleSortClick('sales_num')"
                >
                  <span class="sort-label">销量</span>
                  <div class="sort-arrows">
                    <i
                      class="el-icon-arrow-up"
                      :class="{ active: isSortActive('sales_num', 'asc') }"
                      @click.stop="handleSortClick('sales_num', 'asc')"
                    ></i>
                    <i
                      class="el-icon-arrow-down"
                      :class="{ active: isSortActive('sales_num', 'desc') }"
                      @click.stop="handleSortClick('sales_num', 'desc')"
                    ></i>
                  </div>
                </div>
                <div
                  class="sort-item"
                  :class="{ active: isSortFieldActive('final_price') }"
                  @click="handleSortClick('final_price')"
                >
                  <span class="sort-label">价格</span>
                  <div class="sort-arrows">
                    <i
                      class="el-icon-arrow-up"
                      :class="{ active: isSortActive('final_price', 'asc') }"
                      @click.stop="handleSortClick('final_price', 'asc')"
                    ></i>
                    <i
                      class="el-icon-arrow-down"
                      :class="{ active: isSortActive('final_price', 'desc') }"
                      @click.stop="handleSortClick('final_price', 'desc')"
                    ></i>
                  </div>
                </div>
                <div
                  class="sort-item"
                  :class="{ active: isSortFieldActive('tkrates_raw') }"
                  @click="handleSortClick('tkrates_raw')"
                >
                  <span class="sort-label">佣金比例</span>
                  <div class="sort-arrows">
                    <i
                      class="el-icon-arrow-up"
                      :class="{ active: isSortActive('tkrates_raw', 'asc') }"
                      @click.stop="handleSortClick('tkrates_raw', 'asc')"
                    ></i>
                    <i
                      class="el-icon-arrow-down"
                      :class="{ active: isSortActive('tkrates_raw', 'desc') }"
                      @click.stop="handleSortClick('tkrates_raw', 'desc')"
                    ></i>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </el-card>

        <div v-if="hasActiveFilters" class="filter-result-bar">
          <div class="filter-result-content">
            <i class="el-icon-search"></i>
            <span>当前筛选：</span>
            <el-tag v-for="item in activeFilterItems" :key="item.key" size="small" effect="plain">
              {{ item.label }}
            </el-tag>
            <span class="filter-result-count">共 {{ goodsList.length }} 件已加载商品</span>
          </div>
          <el-button type="text" icon="el-icon-refresh-left" @click="resetFilters">清除筛选</el-button>
        </div>

        <div v-if="libraryTab === 'favorites'" class="favorites-toolbar">
          <span class="favorites-summary">已加载 {{ goodsList.length }} 件收藏商品</span>
          <div class="favorites-actions">
            <el-button :type="manageMode ? 'success' : 'default'" @click="toggleManageMode">
              <i class="el-icon-setting"></i>{{ manageMode ? "完成管理" : "管理" }}
            </el-button>
            <el-button v-if="manageMode" @click="toggleSelectAllCurrentPage">
              {{ isAllCurrentPageSelected ? "取消全选" : "全选所有" }}
            </el-button>
            <el-button
              v-if="manageMode"
              type="danger"
              plain
              :disabled="selectedGoods.length === 0"
              :loading="batchRemoving"
              @click="handleBatchRemoveFavorites"
            >取消收藏（{{ selectedGoods.length }}）</el-button>
          </div>
        </div>

        <!-- 商品列表 -->
        <div class="goods-list" v-loading="loading">
          <div v-if="goodsList.length === 0" class="empty-data">
            {{ loading ? "数据加载中..." : "暂无商品数据" }}
          </div>

          <div class="goods-grid" v-if="goodsList.length > 0">
            <div class="goods-item" v-for="(item, index) in goodsList" :key="`${item.origin_id || index}`">
              <div class="goods-card">
                <div class="goods-image">
                  <el-image :src="item.image || defaultImage" :preview-src-list="[item.image || defaultImage]" fit="contain" lazy>
                    <div slot="error" class="image-slot">
                      <i class="el-icon-picture-outline"></i>
                    </div>
                  </el-image>
                  <el-checkbox
                    v-if="libraryTab === 'favorites' && manageMode"
                    class="goods-select-checkbox"
                    :value="isGoodsSelected(item)"
                    @click.native.stop
                    @change="toggleGoodsSelection(item, $event)"
                  ></el-checkbox>
                  <el-tooltip :content="isGoodsCollected(item) ? '取消收藏' : '收藏商品'" placement="top">
                    <button
                      type="button"
                      class="goods-collect-button"
                      :class="{ active: isGoodsCollected(item) }"
                      :disabled="isCollecting(item)"
                      @click.stop="handleGoodsCollect(item)"
                    >
                      <i :class="isCollecting(item) ? 'el-icon-loading' : isGoodsCollected(item) ? 'el-icon-star-on' : 'el-icon-star-off'"></i>
                    </button>
                  </el-tooltip>
                  <el-tooltip
                    v-if="item.author_name && !(libraryTab === 'favorites' && manageMode)"
                    content="点击搜索该直播间"
                    placement="top"
                  >
                    <button
                      type="button"
                      class="live-badge searchable-text"
                      @click.stop="applyQuickSearch('author_name', item.author_name)"
                    >
                      <img class="live-gif" src="@/assets/image/live.gif" alt="" />
                      <span class="live-author">{{ item.author_name }}</span>
                    </button>
                  </el-tooltip>
                </div>

                <div class="goods-content">
                  <div class="goods-title" :title="item.title" @click="handleOpenGoods(item)">
                    <img
                      v-if="item.shop_title && item.shop_title.includes('旗舰店')"
                      class="flagship-icon"
                      src="https://p3-infra.elabpic.com/tos-cn-i-ax5x5hote5/495598616358408ea09d6ec62f05c4d2~tplv-ax5x5hote5-image.image"
                    />
                    {{ item.title }}
                  </div>

                  <div class="sales-info-container">
                    <el-tooltip
                      v-if="item.shop_title"
                      content="点击搜索该店铺"
                      placement="top"
                    >
                      <button
                        type="button"
                        class="shop-title searchable-text"
                        @click="applyQuickSearch('shop_title', item.shop_title)"
                      >
                        <i class="el-icon-shopping-bag-1"></i>{{ item.shop_title }}
                      </button>
                    </el-tooltip>
                    <!-- <div class="sales-info" v-if="item.sales_num">
                      <span class="sales-text">已售{{ formatSales(item.sales_num) }}</span>
                    </div> -->
                  </div>

                  <div v-if="item.brand_name" class="brand-info-row">
                    <span class="brand-info-label">品牌</span>
                    <el-tooltip content="点击搜索该品牌" placement="top">
                      <button
                        type="button"
                        class="brand-info-value searchable-text"
                        @click="applyQuickSearch('brand_name', item.brand_name)"
                      >
                        {{ item.brand_name }}
                      </button>
                    </el-tooltip>
                  </div>

                  <div class="price-row">
                    <div class="price-info">
                      <div class="current-price">
                        ￥<span class="price-number customFont">{{ item.final_price || item.coupon_price || item.origin_price }}</span>
                      </div>
                      <!-- <div class="original-price">￥{{ item.origin_price }}</div> -->
                    </div>
                    <div class="sales-info" v-if="item.sales_num">
                      <span class="sales-text">已售{{ formatSales(item.sales_num) }}</span>
                    </div>

                    <!-- <div class="goods-tags">
                      <div class="commission-rate">佣金率{{ item.tkrates_raw }}%</div>
                      <div class="commission-text">
                        赚￥<span class="number customFont">{{ formatNumber((item.tkrates_raw * item.final_price) / 100) }}</span>
                      </div>
                    </div> -->
                  </div>
                  <div class="meta-bar">
                    <div class="meta-item meta-item--primary">
                      <span class="meta-label">佣金比例</span>
                      <strong class="meta-value customFont">{{ getIncomeRate(item) }}%</strong>
                    </div>
                    <div class="meta-item meta-item--primary">
                      <span class="meta-label">预估佣金</span>
                      <strong class="meta-value customFont">¥{{ item.fan_price }}</strong>
                    </div>
                  </div>

                  <div class="action-row">
                    <el-button type="primary" style="width: 100%;" @click="handleGoodsClick(item)">立即推广</el-button>
                    <el-button
                      v-if="libraryTab === 'selection'"
                      class="selection-cart-add"
                      :class="{ 'selection-cart-add--added': item.is_in_selection_cart }"
                      :disabled="item.is_in_selection_cart"
                      @click="addToSelectionCart(item, $event)"
                    >
                      <i :class="item.is_in_selection_cart ? 'el-icon-check' : 'el-icon-shopping-cart-2'"></i>
                      {{ item.is_in_selection_cart ? "已加入选品车" : "加入选品车" }}
                    </el-button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <el-badge
      ref="selectionCartEntry"
      class="selection-cart-entry"
      :class="{ 'selection-cart-entry--bounce': selectionCartBouncing }"
      :value="selectionCart.length"
      :hidden="selectionCart.length === 0"
      :max="99"
    >
      <el-tooltip content="打开选品车" placement="left">
        <button type="button" class="selection-cart-float-button" @click="openSelectionCart">
          <i class="el-icon-shopping-cart-2"></i>
          <span>选品车</span>
        </button>
      </el-tooltip>
    </el-badge>

    <el-drawer
      v-if="isMobile"
      title="全部品牌"
      :visible.sync="mobileBrandDrawerVisible"
      direction="btt"
      size="78%"
      append-to-body
      class="mobile-brand-drawer"
      @closed="mobileBrandKeyword = ''"
    >
      <div class="mobile-brand-panel">
        <el-input
          v-model.trim="mobileBrandKeyword"
          class="mobile-brand-search"
          prefix-icon="el-icon-search"
          clearable
          placeholder="搜索品牌名称"
        />
        <div class="mobile-brand-summary">
          <span>{{ mobileBrandKeyword ? `找到 ${mobileDrawerBrandList.length} 个品牌` : `共 ${brandList.length} 个品牌` }}</span>
          <span v-if="brandName" class="mobile-brand-current">当前：{{ brandName }}</span>
        </div>
        <div class="mobile-brand-grid">
          <button
            type="button"
            class="mobile-brand-card mobile-brand-card--all"
            :class="{ active: !brandName }"
            @click="handleMobileBrandChange('')"
          >
            <span class="mobile-brand-all-icon"><i class="el-icon-menu"></i></span>
            <span class="mobile-brand-card-name">全部品牌</span>
          </button>
          <button
            v-for="brand in mobileDrawerBrandList"
            :key="brand.brand_name"
            type="button"
            class="mobile-brand-card"
            :class="{ active: brandName === brand.brand_name }"
            @click="handleMobileBrandChange(brand.brand_name)"
          >
            <span
              class="mobile-brand-favorite"
              :class="{ active: isFavoriteBrand(brand.brand_name) }"
              role="button"
              tabindex="0"
              :aria-label="isFavoriteBrand(brand.brand_name) ? '取消收藏' : '收藏品牌'"
              @click.stop="toggleFavoriteBrand(brand.brand_name)"
              @keydown.enter.stop.prevent="toggleFavoriteBrand(brand.brand_name)"
              @keydown.space.stop.prevent="toggleFavoriteBrand(brand.brand_name)"
            >
              <i :class="isFavoriteBrand(brand.brand_name) ? 'el-icon-star-on' : 'el-icon-star-off'"></i>
            </span>
            <el-image class="mobile-brand-card-logo" :src="brand.logo" fit="contain" lazy>
              <div slot="error" class="brand-logo-error"><i class="el-icon-picture-outline"></i></div>
            </el-image>
            <span class="mobile-brand-card-name">{{ brand.brand_name }}</span>
          </button>
        </div>
        <div v-if="mobileBrandKeyword && !mobileDrawerBrandList.length" class="mobile-brand-empty">
          没有找到相关品牌
        </div>
      </div>
    </el-drawer>

    <el-drawer
      title="我的选品车"
      :visible.sync="selectionCartVisible"
      :size="selectionCartDrawerSize"
      direction="rtl"
      append-to-body
      class="selection-cart-drawer"
    >
      <div class="selection-cart-panel">
        <div class="selection-cart-header">
          <div class="selection-cart-header-main">
            <span>共 {{ selectionCart.length }} 件商品</span>
            <el-checkbox
              v-if="selectionCart.length"
              :value="isAllSelectionCartSelected"
              :indeterminate="isSelectionCartIndeterminate"
              @change="toggleSelectAllSelectionCart"
            >全选</el-checkbox>
          </div>
          <el-button
            type="text"
            class="selection-cart-clear"
            :disabled="selectionCart.length === 0"
            @click="clearSelectionCart"
          >清空选品车</el-button>
        </div>

        <div v-if="selectionCart.length === 0" class="selection-cart-empty">
          <i class="el-icon-shopping-cart-2"></i>
          <span>选品车还是空的</span>
        </div>
        <div v-else class="selection-cart-list">
          <div v-for="item in selectionCart" :key="item.xpid || item.origin_id" class="selection-cart-item">
            <el-checkbox
              class="selection-cart-checkbox"
              :value="isSelectionCartItemSelected(item)"
              @change="toggleSelectionCartItem(item, $event)"
            />
            <el-image class="selection-cart-image" :src="item.image || defaultImage" fit="cover">
              <div slot="error" class="selection-cart-image-error"><i class="el-icon-picture-outline"></i></div>
            </el-image>
            <div class="selection-cart-info">
              <div class="selection-cart-title" :title="item.title">{{ item.title || "未命名商品" }}</div>
              <div class="selection-cart-author">
                <i class="el-icon-video-camera"></i>{{ item.author_name || item.shop_title || "暂无直播间信息" }}
              </div>
              <div class="selection-cart-income">
                <span>佣金比例 <strong>{{ getIncomeRate(item) }}%</strong></span>
                <span>预估佣金 <strong>¥{{ item.fan_price || 0 }}</strong></span>
              </div>
            </div>
            <el-tooltip content="移出选品车" placement="top">
              <button type="button" class="selection-cart-remove" @click="removeFromSelectionCart(item)">
                <i class="el-icon-delete"></i>
              </button>
            </el-tooltip>
          </div>
        </div>
        <div v-if="selectionCart.length" class="selection-cart-footer">
          <el-button
            :disabled="selectedSelectionCartItems.length === 0 || cartBatchCollecting || cartBatchPromoting"
            :loading="cartBatchCollecting"
            @click="batchCollectSelectionCart"
          >一键收藏</el-button>
          <el-button
            type="primary"
            :disabled="selectedSelectionCartItems.length === 0 || cartBatchCollecting || cartBatchPromoting"
            :loading="cartBatchPromoting"
            @click="batchPromoteSelectionCart"
          >一键推广</el-button>
        </div>
      </div>
    </el-drawer>

    <el-dialog
      title="批量推广文案"
      :visible.sync="batchPromoteDialogVisible"
      :width="dialogWidth"
      append-to-body
      class="batch-promote-dialog"
    >
      <div
        v-loading="batchPromoteLoading"
        :element-loading-text="batchPromoteLoadingText"
        class="batch-promote-result"
      >
        <el-input
          v-model="batchPromoteText"
          type="textarea"
          :rows="isMobile ? 13 : 16"
          readonly
          placeholder="推广文案生成后将在这里展示"
        />
        <div class="batch-promote-options">
          <div class="batch-promote-option">
            <div>
              <span class="batch-promote-option__title">商品分割线</span>
              <span class="batch-promote-option__tip">每条商品文案之间显示 ------------------</span>
            </div>
            <el-switch
              v-model="batchPromoteShowDivider"
              :disabled="batchPromoteLoading"
              @change="refreshBatchPromoteText"
            />
          </div>
          <div class="batch-promote-option">
            <div>
              <span class="batch-promote-option__title">显示折扣</span>
              <span class="batch-promote-option__tip">根据四舍五入后的佣金比例换算</span>
            </div>
            <el-switch
              v-model="batchPromoteShowDiscount"
              :disabled="batchPromoteLoading"
              @change="refreshBatchPromoteText"
            />
          </div>
        </div>
      </div>
      <span slot="footer" class="dialog-footer">
        <el-button @click="batchPromoteDialogVisible = false">关闭</el-button>
        <el-button
          type="primary"
          :disabled="!batchPromoteText || batchPromoteLoading"
          @click="copyField('promote_text', batchPromoteText)"
        >一键复制</el-button>
      </span>
    </el-dialog>

    <!-- 推广结果弹窗 -->
    <el-dialog title="推广链接" :visible.sync="promoteDialogVisible" :width="dialogWidth" class="promote-dialog" @close="handlePromoteDialogClose">
      <div v-loading="promoteLoading" class="promote-result">
        <div v-if="!promoteLoading && promoteResult" class="promote-fields">
          <div class="promote-field">
            <label>推广文案</label>
            <div class="field-value">
              <el-input v-model="promoteText" type="textarea" :rows="7" readonly class="field-input promote-copy" />
              <el-button size="small" type="primary" @click="copyField('promote_text', promoteText)">复制</el-button>
            </div>
            <div class="single-promote-option">
              <div>
                <span class="single-promote-option__title">显示折扣</span>
                <span class="single-promote-option__tip">根据四舍五入后的佣金比例换算</span>
              </div>
              <el-switch v-model="promoteShowDiscount" @change="refreshPromoteText" />
            </div>
          </div>
          <div class="promote-field">
            <label>抖音口令</label>
            <div class="field-value">
              <el-input :value="promoteResult.dy_password" type="textarea" :rows="2" readonly class="field-input" />
              <el-button size="small" type="primary" @click="copyField('dy_password', promoteResult.dy_password)">复制</el-button>
            </div>
          </div>
          <div class="promote-field" v-if="dyShortUrl">
            <label>推广链接</label>
            <div class="field-value">
              <el-input :value="dyShortUrl" type="input" readonly class="field-input" />
              <el-button size="small" type="primary" @click="copyField('dy_link', dyShortUrl)">复制</el-button>
            </div>
          </div>
          <el-button v-if="isMobile && promoteResult.dy_deeplink" class="deeplink-button" type="primary" @click="openDyDeeplink">
            前往抖音购买
          </el-button>
        </div>
      </div>
      <span slot="footer" class="dialog-footer">
        <el-button @click="promoteDialogVisible = false">关闭</el-button>
      </span>
    </el-dialog>
  </Layout>
</template>

<script>
import Layout from "@/components/Layout/index";
import {
  getDouyinBrandList,
  getDouyinListCate,
  getDouyinListCpmGoods,
  getDouyinUrl,
  getGoodsCollectList,
  kwebGetShareUrl,
  multiCleanGoodsCollect,
  updateGoodsCollect,
} from "@/api/public";

export default {
  components: {
    Layout,
  },
  data() {
    return {
      libraryTab: "selection",
      categoryList: [],
      categoryId: 0,
      onlyLive: false,
      brandList: [],
      brandRequestSeq: 0,
      brandName: "",
      favoriteBrandNames: [],
      brandsExpanded: false,
      brandListCanCollapse: false,
      mobileInlineBrandLimit: 11,
      mobileBrandDrawerVisible: false,
      mobileBrandKeyword: "",
      sortValue: 0,
      goodsList: [],
      loading: false,
      searchType: "keyword",
      appliedSearchType: "keyword",
      searchKeyword: "",
      appliedSearchKeyword: "",
      searchTypeOptions: [
        { label: "关键词", value: "keyword" },
        { label: "品牌名", value: "brand_name" },
        { label: "店铺名", value: "shop_title" },
        { label: "直播间名", value: "author_name" },
      ],
      suggestTimer: null,
      suggestQuerySeq: 0,
      suggestList: [],
      searchHistory: [],
      searchPanelMode: "none",
      requestId: null,
      pageSize: 40,
      defaultImage: "https://img.huajuanyun.com/utools/2025/711/1752201507730.png",
      promoteDialogVisible: false,
      promoteLoading: false,
      promoteResult: null,
      dyShortUrl: "",
      promoteText: "",
      promoteShowDiscount: false,
      currentPromoteGoods: null,
      isMobile: false,
      collectingIds: [],
      selectionCart: [],
      selectionCartVisible: false,
      selectionCartBouncing: false,
      selectionCartSelectedIds: [],
      cartBatchCollecting: false,
      cartBatchPromoting: false,
      batchPromoteDialogVisible: false,
      batchPromoteLoading: false,
      batchPromoteCurrent: 0,
      batchPromoteTotal: 0,
      batchPromoteText: "",
      batchPromoteItems: [],
      batchPromoteShowDivider: true,
      batchPromoteShowDiscount: false,
      collectionPage: 1,
      collectionHasMore: true,
      manageMode: false,
      selectedGoods: [],
      batchRemoving: false,
    };
  },
  computed: {
    isAllCurrentPageSelected() {
      if (!this.goodsList.length) return false;
      return this.goodsList.every((item) => this.isGoodsSelected(item));
    },
    sortedBrandList() {
      const favoriteSet = new Set(this.favoriteBrandNames);
      return this.brandList
        .map((brand, index) => ({ brand, index }))
        .sort((a, b) => {
          const favoriteDiff = Number(favoriteSet.has(b.brand.brand_name)) - Number(favoriteSet.has(a.brand.brand_name));
          return favoriteDiff || a.index - b.index;
        })
        .map((item) => item.brand);
    },
    inlineBrandList() {
      if (!this.isMobile) return this.sortedBrandList;
      const selectedBrand = this.sortedBrandList.find((brand) => brand.brand_name === this.brandName);
      const remainingBrands = selectedBrand
        ? this.sortedBrandList.filter((brand) => brand.brand_name !== this.brandName)
        : this.sortedBrandList;
      return (selectedBrand ? [selectedBrand, ...remainingBrands] : remainingBrands).slice(0, this.mobileInlineBrandLimit);
    },
    mobileDrawerBrandList() {
      const keyword = this.mobileBrandKeyword.toLowerCase();
      if (!keyword) return this.sortedBrandList;
      return this.sortedBrandList.filter((brand) => String(brand.brand_name || "").toLowerCase().includes(keyword));
    },
    showBrandToggle() {
      return !this.isMobile && this.brandListCanCollapse;
    },
    searchPlaceholder() {
      const placeholderMap = {
        keyword: "支持抖口令、商品链接、商品标题搜索",
        brand_name: "请输入品牌名称",
        shop_title: "请输入店铺名称",
        author_name: "请输入直播间名称",
      };
      return placeholderMap[this.searchType] || "请输入搜索内容";
    },
    activeFilterItems() {
      const items = [];
      const searchOption = this.searchTypeOptions.find((item) => item.value === this.appliedSearchType);
      if (this.appliedSearchKeyword) {
        items.push({
          key: "search",
          label: `${searchOption ? searchOption.label : "关键词"}：${this.appliedSearchKeyword}`,
        });
      }
      if (this.libraryTab === "favorites") return items;
      if (Number(this.categoryId) !== 0) {
        const category = this.categoryList.find((item) => String(item.cat_id) === String(this.categoryId));
        items.push({ key: "category", label: `类目：${category ? category.cat_name : this.categoryId}` });
      }
      if (this.onlyLive) items.push({ key: "live", label: "仅看直播中" });
      if (this.sortValue !== 0) {
        const sortLabels = {
          1: "销量从高到低",
          2: "销量从低到高",
          3: "价格从高到低",
          4: "价格从低到高",
          5: "佣金比例从高到低",
          6: "佣金比例从低到高",
        };
        items.push({ key: "sort", label: `排序：${sortLabels[this.sortValue]}` });
      }
      return items;
    },
    hasActiveFilters() {
      return this.activeFilterItems.length > 0;
    },
    dialogWidth() {
      return this.isMobile ? "92%" : "560px";
    },
    selectionCartDrawerSize() {
      return this.isMobile ? "92%" : "460px";
    },
    selectionCartKeys() {
      return this.selectionCart.map((item) => this.getSelectionCartKey(item)).filter(Boolean);
    },
    selectedSelectionCartItems() {
      const selectedIds = new Set(this.selectionCartSelectedIds);
      return this.selectionCart.filter((item) => selectedIds.has(this.getSelectionCartKey(item)));
    },
    isAllSelectionCartSelected() {
      return this.selectionCart.length > 0 && this.selectedSelectionCartItems.length === this.selectionCart.length;
    },
    isSelectionCartIndeterminate() {
      return this.selectedSelectionCartItems.length > 0 && !this.isAllSelectionCartSelected;
    },
    batchPromoteLoadingText() {
      if (!this.batchPromoteLoading || !this.batchPromoteTotal) return "";
      return `正在生成 ${this.batchPromoteCurrent}/${this.batchPromoteTotal}`;
    },
    isSortActive() {
      return (field, order) => {
        if (field === 'sales_num') return this.sortValue === (order === 'asc' ? 2 : 1);
        if (field === 'final_price') return this.sortValue === (order === 'asc' ? 4 : 3);
        if (field === 'tkrates_raw') return this.sortValue === (order === 'asc' ? 6 : 5);
        return false;
      };
    },
    isSortFieldActive() {
      return (field) => {
        if (field === 'sales_num') return this.sortValue === 1 || this.sortValue === 2;
        if (field === 'final_price') return this.sortValue === 3 || this.sortValue === 4;
        if (field === 'tkrates_raw') return this.sortValue === 5 || this.sortValue === 6;
        return false;
      };
    },
  },
  mounted() {
    this.init();
    window.addEventListener("scroll", this.handleScrollLoadMore, { passive: true });
    window.addEventListener("click", this.bodyCloseMenus);
    window.addEventListener("resize", this.updateDeviceType);
    this.updateDeviceType();
    this.loadHistory();
    this.loadFavoriteBrands();
    this.loadSelectionCart();
  },
  beforeDestroy() {
    window.removeEventListener("scroll", this.handleScrollLoadMore);
    window.removeEventListener("click", this.bodyCloseMenus);
    window.removeEventListener("resize", this.updateDeviceType);
    this.cancelSuggestRequest();
  },
  methods: {
    async init() {
      this.loadLiveStatus();
      await Promise.all([this.getCategoryList(), this.getBrandList()]);
      await this.getGoodsList();
    },
    async getCategoryList() {
      try {
        const response = await getDouyinListCate({});
        if (response && response.data && response.data.list) {
          this.categoryList = response.data.list;
        }
      } catch (error) {
        console.error("获取分类失败:", error);
      }
    },
    async getBrandList() {
      const requestSeq = ++this.brandRequestSeq;
      this.brandList = [];
      this.brandsExpanded = false;
      this.brandListCanCollapse = false;
      try {
        const response = await getDouyinBrandList({
          cate_id: this.categoryId,
          pagesize: 200,
        });
        if (requestSeq !== this.brandRequestSeq) return;
        this.brandList = response && response.data && Array.isArray(response.data.list) ? response.data.list : [];
        this.$nextTick(this.updateBrandCollapseState);
      } catch (error) {
        if (requestSeq !== this.brandRequestSeq) return;
        console.error("获取品牌列表失败:", error);
        this.brandList = [];
      }
    },
    async getGoodsList(loadMore = false) {
      if (this.libraryTab === "favorites") {
        return this.getFavoriteGoodsList(loadMore);
      }
      this.loading = true;
      try {
        const params = {
          cate_id: this.categoryId,
          sort: this.sortValue,
          pagesize: this.pageSize,
          // is_rank: 1
        };
        if (this.appliedSearchKeyword) {
          params[this.appliedSearchType] = this.appliedSearchKeyword;
        } else {
          params.keyword = "";
        }
        if (this.onlyLive) {
          params.is_live = 1;
        }
        if (this.requestId) {
          params.request_id = this.requestId;
        }
        const response = await getDouyinListCpmGoods(params);
        if (response && response.data) {
          const list = response.data.list || [];
          if (loadMore) {
            this.goodsList = [...this.goodsList, ...list];
          } else {
            this.goodsList = list;
          }
          this.syncGoodsSelectionCartState();
          this.requestId = response.data.request_id;
        } else {
          if (!loadMore) {
            this.goodsList = [];
          }
        }
      } catch (error) {
        console.error("获取商品列表失败:", error);
        if (!loadMore) {
          this.goodsList = [];
        }
        this.$message.error("数据加载失败");
      } finally {
        this.loading = false;
      }
    },
    async getFavoriteGoodsList(loadMore = false) {
      if (loadMore && !this.collectionHasMore) return;
      this.loading = true;
      try {
        const targetPage = loadMore ? this.collectionPage + 1 : 1;
        const params = {
          page: targetPage,
          pagesize: this.pageSize,
        };
        if (this.appliedSearchKeyword) params.keyword = this.appliedSearchKeyword;
        const response = await getGoodsCollectList(params);
        const data = response && response.data !== undefined ? response.data : [];
        const list = Array.isArray(data)
          ? data
          : Array.isArray(data.list)
            ? data.list
            : Array.isArray(data.data)
              ? data.data
              : [];
        const normalizedList = list.map((item) => ({ ...item, is_collect: 1 }));
        this.goodsList = loadMore ? [...this.goodsList, ...normalizedList] : normalizedList;
        this.collectionPage = targetPage;
        this.collectionHasMore = list.length >= this.pageSize;
        this.selectedGoods = [];
      } catch (error) {
        console.error("获取收藏列表失败:", error);
        if (!loadMore) this.goodsList = [];
        this.$message.error("收藏列表加载失败");
      } finally {
        this.loading = false;
      }
    },
    handleLibraryTabChange() {
      this.searchType = "keyword";
      this.appliedSearchType = "keyword";
      this.searchKeyword = "";
      this.appliedSearchKeyword = "";
      this.brandName = "";
      this.categoryId = 0;
      this.sortValue = 0;
      this.requestId = null;
      this.collectionPage = 1;
      this.collectionHasMore = true;
      this.manageMode = false;
      this.selectedGoods = [];
      this.closeSearchPanels();
      if (this.libraryTab === "selection") this.getBrandList();
      this.getGoodsList();
    },
    async handleCategoryChange() {
      const selectedCategoryId = String(this.categoryId);
      if (this.appliedSearchType === "brand_name") {
        this.searchType = "keyword";
        this.appliedSearchType = "keyword";
        this.searchKeyword = "";
        this.appliedSearchKeyword = "";
      }
      this.brandName = "";
      this.requestId = null;
      await this.getBrandList();
      if (String(this.categoryId) !== selectedCategoryId) return;
      this.getGoodsList();
    },
    handleLiveStatusChange(value) {
      localStorage.setItem("gaoyong_live_status", value ? "1" : "0");
      this.requestId = null;
      this.getGoodsList();
    },
    loadLiveStatus() {
      const value = localStorage.getItem("gaoyong_live_status");
      this.onlyLive = value === "1" || value === "live";
    },
    handleBrandChange(brandName) {
      const keyword = String(brandName || "").trim();
      if (!keyword && this.appliedSearchType !== "brand_name" && !this.brandName) return;
      this.searchType = keyword ? "brand_name" : "keyword";
      this.appliedSearchType = this.searchType;
      this.searchKeyword = keyword;
      this.appliedSearchKeyword = keyword;
      this.brandName = keyword;
      if (keyword) this.saveHistory(keyword, "brand_name");
      this.closeSearchPanels();
      this.requestId = null;
      this.getGoodsList();
    },
    openMobileBrandDrawer() {
      this.mobileBrandKeyword = "";
      this.mobileBrandDrawerVisible = true;
    },
    handleMobileBrandChange(brandName) {
      this.mobileBrandDrawerVisible = false;
      this.mobileBrandKeyword = "";
      this.handleBrandChange(brandName);
    },
    handleSortClick(field, order) {
      let sortValue = 0;
      if (field === 'comprehensive') {
        sortValue = 0;
      } else if (field === 'sales_num') {
        sortValue = order ? (order === 'asc' ? 2 : 1) : (this.sortValue === 1 ? 2 : 1);
      } else if (field === 'final_price') {
        sortValue = order ? (order === 'asc' ? 4 : 3) : (this.sortValue === 3 ? 4 : 3);
      } else if (field === 'tkrates_raw') {
        sortValue = order ? (order === 'asc' ? 6 : 5) : (this.sortValue === 5 ? 6 : 5);
      }
      if (this.sortValue !== sortValue) {
        this.sortValue = sortValue;
        this.requestId = null;
        this.getGoodsList();
      }
    },
    cancelSuggestRequest() {
      this.suggestQuerySeq += 1;
      if (this.suggestTimer) {
        clearTimeout(this.suggestTimer);
        this.suggestTimer = null;
      }
    },
    closeSearchPanels() {
      this.cancelSuggestRequest();
      this.searchPanelMode = "none";
      this.suggestList = [];
    },
    querySearchAsync(queryString) {
      const keyword = String(queryString || "").trim();
      if (!keyword) {
        this.cancelSuggestRequest();
        this.suggestList = [];
        this.searchPanelMode = "history";
        return;
      }
      const querySeq = ++this.suggestQuerySeq;
      this.suggestList = [];
      if (this.suggestTimer) clearTimeout(this.suggestTimer);
      this.suggestTimer = setTimeout(() => {
        if (querySeq !== this.suggestQuerySeq) {
          return;
        }
        const callbackName = `tbSugCb_${Date.now()}_${Math.floor(Math.random() * 100000)}`;
        const url = `https://suggest.taobao.com/sug?k=1&area=c2c&code=utf-8&q=${encodeURIComponent(keyword)}&callback=${callbackName}`;
        const script = document.createElement("script");
        script.src = url;
        window[callbackName] = (res) => {
          try {
            if (
              querySeq !== this.suggestQuerySeq ||
              this.searchPanelMode !== "suggest" ||
              String(this.searchKeyword || "").trim() !== keyword
            ) {
              return;
            }
            const list = Array.isArray(res && res.result) ? res.result.map((item) => ({ value: item[0] })) : [];
            this.suggestList = list;
          } catch (e) {
            this.suggestList = [];
          } finally {
            delete window[callbackName];
            if (script && script.parentNode) script.parentNode.removeChild(script);
          }
        };
        script.onerror = () => {
          delete window[callbackName];
          if (script && script.parentNode) script.parentNode.removeChild(script);
          if (querySeq === this.suggestQuerySeq) {
            this.suggestList = [];
          }
        };
        document.body.appendChild(script);
      }, 200);
    },
    handleSearch() {
      if (!this.searchKeyword || !this.searchKeyword.trim()) {
        this.$message.warning("请输入搜索关键词");
        return;
      }
      const keyword = this.searchKeyword.trim();
      if (this.libraryTab === "favorites") this.searchType = "keyword";
      this.appliedSearchType = this.searchType;
      this.appliedSearchKeyword = keyword;
      this.brandName = this.libraryTab === "selection" && this.searchType === "brand_name" ? keyword : "";
      // 记录搜索历史
      this.saveHistory(keyword, this.searchType);
      this.closeSearchPanels();
      this.requestId = null;
      this.getGoodsList();
    },
    handleSearchClear() {
      this.searchKeyword = "";
      this.appliedSearchKeyword = "";
      this.appliedSearchType = this.searchType;
      this.brandName = "";
      this.cancelSuggestRequest();
      this.suggestList = [];
      this.searchPanelMode = "history";
      this.requestId = null;
      this.getGoodsList();
    },
    clearSearchFilter() {
      this.handleSearchClear();
    },
    handleSearchTypeChange() {
      this.closeSearchPanels();
    },
    handleSearchFocus() {
      // 聚焦时优先显示搜索历史；开始输入后再切到联想列表。
      this.cancelSuggestRequest();
      this.suggestList = [];
      this.searchPanelMode = "history";
    },
    handleSearchInput(val) {
      // 输入时切到联想列表；输入框清空后重新显示历史。
      const keyword = String(val || "").trim();
      if (!keyword) {
        this.cancelSuggestRequest();
        this.suggestList = [];
        this.searchPanelMode = "history";
        return;
      }
      if (this.searchType === "keyword") {
        this.searchPanelMode = "suggest";
        this.querySearchAsync(keyword);
      } else {
        this.closeSearchPanels();
      }
    },
    handleSearchBlur() {
      // 延迟关闭，避免点击历史或联想项时先触发 blur 导致点击无效。
      setTimeout(() => {
        this.closeSearchPanels();
      }, 150);
    },
    fastSearch(val, type = "keyword") {
      this.searchType = this.libraryTab === "favorites" ? "keyword" : type;
      this.searchKeyword = val;
      this.sortValue = 0;
      this.handleSearch();
      this.$nextTick(() => {
        if (this.$refs.searchInput && this.$refs.searchInput.blur) {
          this.$refs.searchInput.blur();
        }
      });
    },
    applyQuickSearch(type, value) {
      const keyword = String(value || "").trim();
      if (!keyword) return;
      this.searchType = this.libraryTab === "favorites" ? "keyword" : type;
      this.searchKeyword = keyword;
      this.sortValue = 0;
      this.handleSearch();
    },
    resetFilters() {
      this.categoryId = 0;
      this.onlyLive = false;
      this.sortValue = 0;
      this.searchType = "keyword";
      this.appliedSearchType = "keyword";
      this.searchKeyword = "";
      this.appliedSearchKeyword = "";
      this.brandName = "";
      localStorage.setItem("gaoyong_live_status", "0");
      this.closeSearchPanels();
      this.requestId = null;
      this.getBrandList();
      this.getGoodsList();
    },
    isGoodsCollected(item) {
      return this.libraryTab === "favorites" || Number(item && item.is_collect) === 1;
    },
    isCollecting(item) {
      return Boolean(item && this.collectingIds.includes(String(item.origin_id)));
    },
    async handleGoodsCollect(item) {
      if (!item || !item.origin_id || this.isCollecting(item)) return;
      if (this.libraryTab === "favorites") {
        await this.removeFavoriteGoods(item);
        return;
      }
      const originId = String(item.origin_id);
      const nextCollectStatus = this.isGoodsCollected(item) ? 0 : 1;
      this.collectingIds = [...this.collectingIds, originId];
      try {
        await updateGoodsCollect({
          is_collect: nextCollectStatus,
          goods_source: 1,
          origin_id: item.origin_id,
        });
        this.$set(item, "is_collect", nextCollectStatus);
        this.$message.success(nextCollectStatus === 1 ? "收藏成功" : "已取消收藏");
      } catch (error) {
        console.error("更新商品收藏状态失败:", error);
      } finally {
        this.collectingIds = this.collectingIds.filter((id) => id !== originId);
      }
    },
    getCollectId(item) {
      return item && (item.collect_ids || item.id);
    },
    async removeFavoriteGoods(item) {
      const collectId = this.getCollectId(item);
      if (!collectId) {
        this.$message.warning("该商品缺少收藏记录ID，无法取消收藏");
        return;
      }
      const originId = String(item.origin_id);
      this.collectingIds = [...this.collectingIds, originId];
      try {
        await multiCleanGoodsCollect({ collect_ids: collectId });
        this.goodsList = this.goodsList.filter((goods) => this.getCollectId(goods) !== collectId);
        this.selectedGoods = this.selectedGoods.filter((goods) => this.getCollectId(goods) !== collectId);
        this.$message.success("已取消收藏");
      } catch (error) {
        console.error("取消收藏失败:", error);
        this.$message.error("取消收藏失败，请稍后重试");
      } finally {
        this.collectingIds = this.collectingIds.filter((id) => id !== originId);
      }
    },
    toggleManageMode() {
      this.manageMode = !this.manageMode;
      this.selectedGoods = [];
    },
    isGoodsSelected(item) {
      const collectId = String(this.getCollectId(item));
      return this.selectedGoods.some((goods) => String(this.getCollectId(goods)) === collectId);
    },
    toggleGoodsSelection(item, selected) {
      const collectId = String(this.getCollectId(item));
      if (selected && !this.isGoodsSelected(item)) {
        this.selectedGoods = [...this.selectedGoods, item];
      } else if (!selected) {
        this.selectedGoods = this.selectedGoods.filter(
          (goods) => String(this.getCollectId(goods)) !== collectId
        );
      }
    },
    toggleSelectAllCurrentPage() {
      this.selectedGoods = this.isAllCurrentPageSelected ? [] : [...this.goodsList];
    },
    async handleBatchRemoveFavorites() {
      const collectIds = [...new Set(this.selectedGoods.map((item) => this.getCollectId(item)).filter(Boolean))];
      if (!collectIds.length) return;
      try {
        await this.$confirm(`确定取消收藏选中的 ${collectIds.length} 件商品吗？`, "批量取消收藏", {
          type: "warning",
        });
      } catch (error) {
        return;
      }
      this.batchRemoving = true;
      try {
        await multiCleanGoodsCollect({ collect_ids: collectIds.join(",") });
        const idSet = new Set(collectIds.map(String));
        this.goodsList = this.goodsList.filter((item) => !idSet.has(String(this.getCollectId(item))));
        this.selectedGoods = [];
        this.$message.success("批量取消收藏成功");
      } catch (error) {
        console.error("批量取消收藏失败:", error);
        this.$message.error("批量取消收藏失败，请稍后重试");
      } finally {
        this.batchRemoving = false;
      }
    },
    bodyCloseMenus(e) {
      if (this.$refs.searchBox && !this.$refs.searchBox.contains(e.target)) {
        this.closeSearchPanels();
      }
    },
    loadHistory() {
      const key = "gaoyong_search_history";
      try {
        const history = JSON.parse(localStorage.getItem(key) || "[]");
        this.searchHistory = Array.isArray(history)
          ? history.map((item) => this.normalizeHistoryItem(item)).filter(Boolean)
          : [];
        // 读取时同步升级旧版字符串记录，之后每条历史都会带搜索类型。
        localStorage.setItem(key, JSON.stringify(this.searchHistory));
      } catch (error) {
        this.searchHistory = [];
      }
    },
    normalizeHistoryItem(item) {
      if (typeof item === "string") {
        const keyword = item.trim();
        return keyword ? { keyword, type: "keyword" } : null;
      }
      if (!item || typeof item !== "object") return null;
      const keyword = String(item.keyword || "").trim();
      if (!keyword) return null;
      const validTypes = this.searchTypeOptions.map((option) => option.value);
      return {
        keyword,
        type: validTypes.includes(item.type) ? item.type : "keyword",
      };
    },
    getSearchTypeLabel(type) {
      const option = this.searchTypeOptions.find((item) => item.value === type);
      return option ? option.label : "关键词";
    },
    getSearchTypeTagType(type) {
      const typeMap = {
        keyword: "info",
        brand_name: "danger",
        shop_title: "success",
        author_name: "warning",
      };
      return typeMap[type] || "info";
    },
    saveHistory(keyword, type = "keyword") {
      if (!keyword || !keyword.trim()) return;
      const key = "gaoyong_search_history";
      const value = this.normalizeHistoryItem({ keyword, type });
      let history = [];
      try {
        const storedHistory = JSON.parse(localStorage.getItem(key) || "[]");
        history = Array.isArray(storedHistory)
          ? storedHistory.map((item) => this.normalizeHistoryItem(item)).filter(Boolean)
          : [];
      } catch (error) {
        history = [];
      }
      history = history.filter((item) => !(item.keyword === value.keyword && item.type === value.type));
      history.unshift(value);
      history = history.slice(0, 10);
      localStorage.setItem(key, JSON.stringify(history));
      this.searchHistory = history;
    },
    clearHistory() {
      const key = "gaoyong_search_history";
      localStorage.removeItem(key);
      this.searchHistory = [];
    },
    loadFavoriteBrands() {
      const key = "gaoyong_favorite_brands";
      try {
        const favorites = JSON.parse(localStorage.getItem(key) || "[]");
        this.favoriteBrandNames = Array.isArray(favorites)
          ? [...new Set(favorites.filter((name) => typeof name === "string" && name.trim()))]
          : [];
      } catch (error) {
        this.favoriteBrandNames = [];
      }
    },
    isFavoriteBrand(brandName) {
      return this.favoriteBrandNames.includes(brandName);
    },
    toggleFavoriteBrand(brandName) {
      if (!brandName) return;
      if (this.isFavoriteBrand(brandName)) {
        this.favoriteBrandNames = this.favoriteBrandNames.filter((name) => name !== brandName);
      } else {
        this.favoriteBrandNames = [...this.favoriteBrandNames, brandName];
      }
      localStorage.setItem("gaoyong_favorite_brands", JSON.stringify(this.favoriteBrandNames));
    },
    loadSelectionCart() {
      try {
        const storedCart = JSON.parse(localStorage.getItem("gaoyong_selection_cart") || "[]");
        if (!Array.isArray(storedCart)) {
          this.selectionCart = [];
          return;
        }
        this.selectionCart = this.dedupeSelectionCart(storedCart);
        this.saveSelectionCart();
        this.syncGoodsSelectionCartState();
      } catch (error) {
        this.selectionCart = [];
      }
    },
    saveSelectionCart() {
      this.selectionCart = this.dedupeSelectionCart(this.selectionCart);
      localStorage.setItem("gaoyong_selection_cart", JSON.stringify(this.selectionCart));
    },
    dedupeSelectionCart(items) {
      const uniqueGoods = new Map();
      (Array.isArray(items) ? items : []).forEach((item, index) => {
        const originId = this.normalizeGoodsId(item && item.origin_id);
        if (!originId) return;
        const normalizedItem = {
          ...item,
          origin_id: originId,
          xpid: this.normalizeXpid(item && item.xpid),
          // 兼容旧版未记录加入时间的数据：旧数组为按加入顺序追加，索引越大越新。
          added_at: Number(item && item.added_at) || index + 1,
        };
        const goodsKey = this.getSelectionCartKey(normalizedItem);
        if (!goodsKey) return;
        const existingItem = uniqueGoods.get(goodsKey);
        if (!existingItem || normalizedItem.added_at >= existingItem.added_at) {
          uniqueGoods.set(goodsKey, normalizedItem);
        }
      });
      return [...uniqueGoods.values()].sort((a, b) => Number(b.added_at) - Number(a.added_at));
    },
    normalizeXpid(xpid) {
      if (xpid === undefined || xpid === null) return "";
      return String(xpid).trim();
    },
    normalizeGoodsId(originId) {
      if (originId === undefined || originId === null) return "";
      const normalizedId = String(originId).trim();
      try {
        return decodeURIComponent(normalizedId);
      } catch (error) {
        return normalizedId;
      }
    },
    getSelectionCartKey(item) {
      const xpid = this.normalizeXpid(item && item.xpid);
      if (xpid) return `xpid:${xpid}`;
      // 仅用于兼容接口透出 xpid 前已经保存到本地的旧数据。
      const originId = this.normalizeGoodsId(item && item.origin_id);
      return originId ? `legacy-origin:${originId}` : "";
    },
    getLegacyCartFingerprint(item) {
      const title = String(item && item.title || "").trim();
      const image = String(item && item.image || "").trim();
      return title && image ? `${title}@@${image}` : "";
    },
    isInSelectionCart(item) {
      const goodsKey = this.getSelectionCartKey(item);
      return Boolean(goodsKey) && this.selectionCartKeys.includes(goodsKey);
    },
    syncGoodsSelectionCartState() {
      const goodsByXpid = new Map();
      const goodsByOriginId = new Map();
      const goodsByFingerprint = new Map();
      this.goodsList.forEach((item) => {
        const xpid = this.normalizeXpid(item && item.xpid);
        const originId = this.normalizeGoodsId(item && item.origin_id);
        const fingerprint = this.getLegacyCartFingerprint(item);
        if (xpid) goodsByXpid.set(xpid, item);
        if (originId) goodsByOriginId.set(originId, item);
        if (fingerprint) goodsByFingerprint.set(fingerprint, item);
      });

      let cartChanged = false;
      this.selectionCart = this.dedupeSelectionCart(this.selectionCart.map((cartItem) => {
        const xpid = this.normalizeXpid(cartItem && cartItem.xpid);
        const originId = this.normalizeGoodsId(cartItem && cartItem.origin_id);
        const fingerprint = this.getLegacyCartFingerprint(cartItem);
        const currentGoods = (xpid && goodsByXpid.get(xpid))
          || (originId && goodsByOriginId.get(originId))
          || (fingerprint && goodsByFingerprint.get(fingerprint));
        if (!currentGoods) return cartItem;

        const currentXpid = this.normalizeXpid(currentGoods.xpid);
        const currentOriginId = this.normalizeGoodsId(currentGoods.origin_id);
        if (xpid === currentXpid && originId === currentOriginId) return cartItem;
        cartChanged = true;
        return {
          ...cartItem,
          xpid: currentXpid || xpid,
          // origin_id 仍需保持最新值，供收藏和推广接口调用。
          origin_id: currentOriginId || originId,
        };
      }));
      if (cartChanged) this.saveSelectionCart();

      const cartKeySet = new Set(this.selectionCartKeys);
      this.goodsList.forEach((item) => {
        const isInCart = cartKeySet.has(this.getSelectionCartKey(item));
        this.$set(item, "is_in_selection_cart", isInCart);
      });
    },
    addToSelectionCart(item, event) {
      const originId = this.normalizeGoodsId(item && item.origin_id);
      if (!originId) {
        this.$message.warning("商品ID不存在，无法加入选品车");
        return;
      }
      if (this.isInSelectionCart(item)) {
        this.$set(item, "is_in_selection_cart", true);
        return;
      }
      this.animateGoodsToSelectionCart(item, event);
      this.selectionCart = [
        {
          origin_id: originId,
          xpid: this.normalizeXpid(item.xpid),
          image: item.image || "",
          title: item.title || "",
          author_name: item.author_name || "",
          shop_title: item.shop_title || "",
          fan_price_tkrates: item.fan_price_tkrates || "",
          tkrates_raw: item.tkrates_raw || "",
          tkrates_self: item.tkrates_self || "",
          fan_price: item.fan_price || 0,
          goods_source: 1,
          added_at: Date.now(),
        },
        ...this.selectionCart,
      ];
      this.saveSelectionCart();
      this.syncGoodsSelectionCartState();
      this.$message.success("已加入选品车");
    },
    removeFromSelectionCart(targetItem) {
      const targetKey = this.getSelectionCartKey(targetItem);
      this.selectionCart = this.selectionCart.filter((item) => this.getSelectionCartKey(item) !== targetKey);
      this.selectionCartSelectedIds = this.selectionCartSelectedIds.filter((id) => id !== targetKey);
      this.saveSelectionCart();
      this.syncGoodsSelectionCartState();
    },
    openSelectionCart() {
      this.selectionCartSelectedIds = [];
      this.selectionCartVisible = true;
    },
    isSelectionCartItemSelected(item) {
      return this.selectionCartSelectedIds.includes(this.getSelectionCartKey(item));
    },
    toggleSelectionCartItem(item, selected) {
      const goodsKey = this.getSelectionCartKey(item);
      if (!goodsKey) return;
      if (selected) {
        this.selectionCartSelectedIds = [...new Set([...this.selectionCartSelectedIds, goodsKey])];
      } else {
        this.selectionCartSelectedIds = this.selectionCartSelectedIds.filter((id) => id !== goodsKey);
      }
    },
    toggleSelectAllSelectionCart(selected) {
      this.selectionCartSelectedIds = selected ? [...this.selectionCartKeys] : [];
    },
    async batchCollectSelectionCart() {
      const items = [...this.selectedSelectionCartItems];
      if (!items.length || this.cartBatchCollecting) return;
      this.cartBatchCollecting = true;
      try {
        const results = await Promise.all(items.map(async (item) => {
          try {
            await updateGoodsCollect({
              is_collect: 1,
              goods_source: Number(item.goods_source) || 1,
              origin_id: item.origin_id,
            });
            return { item, success: true };
          } catch (error) {
            console.error(`收藏商品 ${item.origin_id} 失败:`, error);
            return { item, success: false };
          }
        }));
        const successItems = results.filter((result) => result.success).map((result) => result.item);
        const successKeys = new Set(successItems.map((item) => this.getSelectionCartKey(item)));
        this.goodsList.forEach((goods) => {
          if (successKeys.has(this.getSelectionCartKey(goods))) this.$set(goods, "is_collect", 1);
        });
        successItems.forEach((item) => this.$set(item, "is_collect", 1));
        if (successItems.length === items.length) {
          this.$message.success(`已收藏 ${successItems.length} 件商品`);
        } else if (successItems.length) {
          this.$message.warning(`收藏成功 ${successItems.length} 件，失败 ${items.length - successItems.length} 件`);
        } else {
          this.$message.error("批量收藏失败，请稍后重试");
        }
      } finally {
        this.cartBatchCollecting = false;
        this.selectionCartVisible = false;
      }
    },
    async batchPromoteSelectionCart() {
      const items = [...this.selectedSelectionCartItems];
      if (!items.length || this.cartBatchPromoting) return;
      this.cartBatchPromoting = true;
      this.batchPromoteLoading = true;
      this.selectionCartVisible = false;
      this.batchPromoteDialogVisible = true;
      this.batchPromoteCurrent = 0;
      this.batchPromoteTotal = items.length;
      this.batchPromoteText = "";
      this.batchPromoteItems = [];
      let failedCount = 0;
      try {
        for (let index = 0; index < items.length; index += 1) {
          const item = items[index];
          this.batchPromoteCurrent = index + 1;
          try {
            const link = await this.getSelectionCartPromotionLink(item);
            this.batchPromoteItems.push({ item, link });
          } catch (error) {
            failedCount += 1;
            console.error(`生成商品 ${item.origin_id} 推广链接失败:`, error);
          }
        }
        this.refreshBatchPromoteText();
        if (!this.batchPromoteItems.length) {
          this.$message.error("所选商品的推广链接均生成失败，请稍后重试");
        } else if (failedCount) {
          this.$message.warning(`已生成 ${this.batchPromoteItems.length} 件，失败 ${failedCount} 件`);
        } else {
          this.$message.success(`已生成 ${this.batchPromoteItems.length} 件商品的推广文案`);
        }
      } finally {
        this.batchPromoteLoading = false;
        this.cartBatchPromoting = false;
      }
    },
    refreshBatchPromoteText() {
      const separator = this.batchPromoteShowDivider ? "\n------------------------------------------\n" : "\n\n";
      this.batchPromoteText = this.batchPromoteItems
        .map(({ item, link }) => this.buildBatchPromoteItemText(item, link))
        .join(separator);
    },
    buildBatchPromoteItemText(item, link) {
      const lines = [
        `【商品名称】 ${item && item.title || ""}`,
        `【直播间】 ${item && (item.author_name || item.shop_title) || ""}`,
      ];
      if (this.batchPromoteShowDiscount) {
        lines.push(`【折扣】 ${this.getCommissionDiscount(item)}`);
      }
      lines.push(`【下单链接】 ${link || ""}`);
      return lines.join("\n");
    },
    getCommissionDiscount(item) {
      const commissionRate = parseFloat(item && (item.fan_price_tkrates || item.tkrates_raw) || 0);
      const roundedRate = Math.round(Math.min(100, Math.max(0, Number.isNaN(commissionRate) ? 0 : commissionRate)));
      const discount = (100 - roundedRate) / 10;
      return `${discount.toFixed(1).replace(/\.0$/, "")}折`;
    },
    async getSelectionCartPromotionLink(item) {
      const promoteResponse = await getDouyinUrl({ origin_id: item.origin_id });
      if (!promoteResponse || !promoteResponse.data) throw new Error("获取推广信息失败");
      const path = `xid=${window.xid}&id=${item.origin_id}#/pages/marketing/douyin/goods`;
      const shareResponse = await kwebGetShareUrl({ path });
      const shortUrl = shareResponse && shareResponse.data && shareResponse.data.short_url;
      if (!shortUrl) throw new Error("获取推广链接失败");
      return shortUrl;
    },
    async clearSelectionCart() {
      if (!this.selectionCart.length) return;
      try {
        await this.$confirm(`确定清空选品车中的 ${this.selectionCart.length} 件商品吗？`, "清空选品车", {
          type: "warning",
        });
      } catch (error) {
        return;
      }
      this.selectionCart = [];
      this.selectionCartSelectedIds = [];
      this.saveSelectionCart();
      this.syncGoodsSelectionCartState();
      this.selectionCartVisible = false;
      this.$message.success("选品车已清空");
    },
    animateGoodsToSelectionCart(item, event) {
      const entryRef = this.$refs.selectionCartEntry;
      const target = entryRef && (entryRef.$el || entryRef);
      const trigger = event && event.currentTarget;
      const card = trigger && trigger.closest ? trigger.closest(".goods-card") : null;
      const source = card ? card.querySelector(".goods-image") : trigger;
      if (!target || !source) {
        this.triggerSelectionCartBounce();
        return;
      }
      const sourceRect = source.getBoundingClientRect();
      const targetRect = target.getBoundingClientRect();
      const flyer = document.createElement("div");
      const size = this.isMobile ? 42 : 54;
      Object.assign(flyer.style, {
        position: "fixed",
        zIndex: "10000",
        left: `${sourceRect.left + sourceRect.width / 2 - size / 2}px`,
        top: `${sourceRect.top + sourceRect.height / 2 - size / 2}px`,
        width: `${size}px`,
        height: `${size}px`,
        borderRadius: "50%",
        border: "2px solid #fff",
        backgroundColor: "#fff",
        backgroundImage: `url(${item.image || this.defaultImage})`,
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
        backgroundSize: "cover",
        boxShadow: "0 5px 18px rgba(0, 0, 0, 0.25)",
        pointerEvents: "none",
        opacity: "1",
        transform: "translate3d(0, 0, 0) scale(1)",
        transition: `transform ${this.isMobile ? 520 : 680}ms cubic-bezier(.22,.8,.35,1), opacity 180ms ease-out`,
      });
      document.body.appendChild(flyer);
      const deltaX = targetRect.left + targetRect.width / 2 - (sourceRect.left + sourceRect.width / 2);
      const deltaY = targetRect.top + targetRect.height / 2 - (sourceRect.top + sourceRect.height / 2);
      let finished = false;
      const finish = () => {
        if (finished) return;
        finished = true;
        if (flyer.parentNode) flyer.parentNode.removeChild(flyer);
        this.triggerSelectionCartBounce();
      };
      flyer.addEventListener("transitionend", finish, { once: true });
      window.requestAnimationFrame(() => {
        window.requestAnimationFrame(() => {
          flyer.style.transform = `translate3d(${deltaX}px, ${deltaY}px, 0) scale(.22)`;
          flyer.style.opacity = ".35";
        });
      });
      setTimeout(finish, this.isMobile ? 650 : 820);
    },
    triggerSelectionCartBounce() {
      this.selectionCartBouncing = false;
      this.$nextTick(() => {
        this.selectionCartBouncing = true;
        setTimeout(() => {
          this.selectionCartBouncing = false;
        }, 420);
      });
    },
    handleScrollLoadMore() {
      if (this.loading) return;
      if (this.libraryTab === "favorites" && !this.collectionHasMore) return;
      if (this.libraryTab === "selection" && !this.requestId) return;
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop || document.body.scrollTop || 0;
      const viewportHeight = window.innerHeight || document.documentElement.clientHeight || document.body.clientHeight;
      const fullHeight = document.documentElement.scrollHeight || document.body.scrollHeight;
      if (scrollTop + viewportHeight + 200 >= fullHeight) {
        this.getGoodsList(true);
      }
    },
    async handleGoodsClick(item) {
      if (!item.origin_id) {
        this.$message.warning("商品ID不存在");
        return;
      }
      this.promoteLoading = true;
      this.promoteDialogVisible = true;
      this.promoteResult = null;
      this.dyShortUrl = "";
      this.promoteText = "";
      this.currentPromoteGoods = item;
      try {
        const response = await getDouyinUrl({ origin_id: item.origin_id });
        if (response && response.data) {
          this.promoteResult = {
            dy_password: response.data.dy_password || "",
            dy_deeplink: response.data.dy_deeplink || "",
          };
          this.promoteText = this.buildPromoteText(item, this.dyShortUrl);
        } else {
          this.$message.error("获取推广链接失败");
        }
      } catch (error) {
        console.error("获取推广链接失败:", error);
        this.$message.error("获取推广链接失败，请稍后重试");
      } finally {
        this.promoteLoading = false;
      }
      this.getDyWebUrl(item)
    },
    getDyWebUrl (item) {
      let path = `xid=${window.xid}&id=${item.origin_id}#/pages/marketing/douyin/goods`
      kwebGetShareUrl({
        path
      }).then(res => {
        this.dyShortUrl = res.data.short_url || "";
        this.promoteText = this.buildPromoteText(item, this.dyShortUrl);
      })
    },
    handleOpenGoods(item) {
      if (!item.detail_url) {
        this.$message.warning("商品详情页链接不存在");
        return;
      }
      window.open(item.detail_url, "_blank");
    },
    copyField(fieldName, value) {
      if (!value) {
        this.$message.warning("内容为空，无法复制");
        return;
      }
      this.$copyText(value).then(
        () => this.$message.success(`${this.getCopyLabel(fieldName)}复制成功`),
        () => this.$message.error("复制失败，请手动复制")
      );
    },
    handlePromoteDialogClose() {
      this.promoteResult = null;
      this.promoteText = "";
      this.currentPromoteGoods = null;
    },
    updateDeviceType() {
      this.isMobile = window.innerWidth <= 768;
      if (!this.isMobile) this.mobileBrandDrawerVisible = false;
      this.$nextTick(this.updateBrandCollapseState);
    },
    updateBrandCollapseState() {
      const brandList = this.$refs.brandList;
      this.brandListCanCollapse = !this.isMobile && Boolean(brandList && brandList.scrollHeight > 234);
    },
    getCopyLabel(fieldName) {
      const labelMap = {
        dy_password: "抖音口令",
        dy_link: "推广链接",
        dy_deeplink: "Deeplink",
        promote_text: "推广文案",
      };
      return labelMap[fieldName] || "内容";
    },
    openDyDeeplink() {
      const link = this.promoteResult && this.promoteResult.dy_deeplink;
      if (!link) {
        this.$message.warning("Deeplink为空，无法打开");
        return;
      }
      window.location.href = link;
    },
    buildPromoteText(item, link) {
      if (!item) return "";
      const lines = [
        `【商品名称】 ${item.title || ""}`,
        `【直播间】 ${item.author_name || item.shop_title || ""}`,
      ];
      if (this.promoteShowDiscount) {
        lines.push(`【折扣】 ${this.getCommissionDiscount(item)}`);
      }
      lines.push(`【下单链接】 ${link || ""}`);
      return lines.join("\n");
    },
    refreshPromoteText() {
      this.promoteText = this.buildPromoteText(this.currentPromoteGoods, this.dyShortUrl);
    },
    getIncomeRate(item) {
      if (!item) return 0;
      const rate = parseFloat(item.fan_price_tkrates || item.tkrates_raw || 0);
      return (isNaN(rate) ? 0 : rate).toFixed(1);
    },
    formatNumber(num) {
      if (!num) return "0";
      const number = parseFloat(num);
      return isNaN(number) ? "0" : number.toFixed(2);
    },
    formatSales(num) {
      if (!num) return "0";
      const number = Number(num);
      if (isNaN(number)) return num || "0";
      return number >= 10000 ? (number / 10000).toFixed(1) + "万" : number.toString();
    },
    extractRealGoodsId(originId) {
      if (!originId) return "-";
      try {
        const decodedId = decodeURIComponent(originId);
        const parts = decodedId.split("|");
        return parts.length >= 2 ? parts[1] : originId;
      } catch (error) {
        return originId;
      }
    },
    copyGoodsId(id) {
      if (!id) return;
      const realId = this.extractRealGoodsId(id);
      this.$copyText(realId).then(
        () => this.$message.success("商品ID复制成功"),
        () => this.$message.error("复制失败，请手动复制")
      );
    },
    copyGoodsLink(link) {
      if (!link) return;
      this.$copyText(link).then(
        () => this.$message.success("商品链接复制成功"),
        () => this.$message.error("复制失败，请手动复制")
      );
    },
  },
};
</script>

<style lang="scss" scoped>
.dygaoyong-page {
  padding: 20px;
  min-height: calc(100vh - 60px);
  background-color: #f8f9fa;
}

.page-header {
  display: flex;
  justify-content: center;
  align-items: center;
  margin: 40px 0 60px;

  .header-search {
    width: 100%;
    max-width: 700px;

    .search-box {
      position: relative;
      width: 100%;
      max-width: 100%;
      box-sizing: border-box;

      .history-box,
      .suggest-box {
        position: absolute;
        top: 100%;
        left: 0;
        width: 100%;
        background: #fff;
        box-shadow: 2px 2px 5px rgba(100, 100, 100, 0.1), -2px 2px 5px rgba(100, 100, 100, 0.1);
        border-radius: 0 0 10px 10px;
        z-index: 9999;

        .history-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 10px 20px 5px;
          color: #999;
          font-size: 13px;

          .history-clear {
            font-size: 12px;
            cursor: pointer;
            color: #999;

            &:hover {
              color: #666;
            }
          }
        }

        ul {
          margin: 0;
          padding: 0;
          list-style: none;
          max-height: 260px;
          overflow-y: auto;

          .history-item,
          .suggest-item {
            padding: 8px 20px;
            cursor: pointer;
            color: #666;
            font-size: 14px;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;

            &:hover {
              background: #f2f2f2;
            }

            .el-icon-time,
            .el-icon-search {
              margin-right: 6px;
              vertical-align: middle;
            }
          }

          .history-item {
            display: flex;
            align-items: center;
            justify-content: space-between;
            gap: 12px;

            .history-item-main {
              display: flex;
              align-items: center;
              min-width: 0;
            }

            .history-keyword {
              overflow: hidden;
              text-overflow: ellipsis;
              white-space: nowrap;
            }

            ::v-deep .el-tag {
              flex: 0 0 auto;
            }
          }
        }
      }
    }

    .search-row {
      display: flex;
      align-items: stretch;
      width: 100%;
      height: 48px;
      border: 1px solid #dcdfe6;
      border-radius: 4px;
      overflow: hidden;
      background: #fff;
      box-sizing: border-box;
    }

    .search-input-large {
      flex: 1 1 auto;
      min-width: 0;

      ::v-deep .el-input__inner {
        height: 46px;
        border: none;
        border-radius: 0;
        font-size: 16px;
        padding: 0 15px;
      }
    }

    .search-type-select {
      flex: 0 0 112px;
      width: 112px;
      border-right: 1px solid #dcdfe6;

      ::v-deep .el-input__inner {
        height: 46px;
        border: none;
        border-radius: 0;
        padding-left: 14px;
        font-size: 14px;
      }
    }

    .search-button {
      flex: 0 0 100px;
      width: 100px;
      height: 48px;
      margin: -1px -1px -1px 0;
      border: none;
      border-radius: 0 4px 4px 0;
      color: #ffffff;
      font-size: 16px;
      background-color: var(--main-bg-color);

      &:hover {
        background-color: #3688cc;
      }

      &:active {
        background-color: #2c75a3;
      }
    }

  }
}

.filter-result-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin: 0 0 16px;
  padding: 10px 16px;
  border: 1px solid #d9ecff;
  border-radius: 8px;
  background: #f4faff;
  color: #606266;
  font-size: 14px;

  .filter-result-content {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 8px;
    min-width: 0;
  }

  .el-icon-search {
    color: var(--main-bg-color);
  }

  .filter-result-count {
    color: #909399;
  }
}

.live-badge {
  position: absolute;
  top: 6px;
  right: 6px;
  z-index: 1;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 3px 8px;
  background: rgba(0, 0, 0, 0.65);
  border-radius: 4px;
  backdrop-filter: blur(4px);
  max-width: calc(100% - 12px);
  min-width: 0;
  box-sizing: border-box;
  overflow: hidden;
  border: none;
  cursor: pointer;

  .live-gif {
    width: 18px;
    height: 18px;
    border-radius: 50%;
    flex-shrink: 0;
  }

  .live-author {
    flex: 1;
    min-width: 0;
    color: #fff;
    font-size: 13px;
    line-height: 1;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
}

.library-tabs {
  margin-bottom: 18px;

  ::v-deep .el-tabs__header {
    margin: 0;
  }

  ::v-deep .el-tabs__item {
    height: 42px;
    padding: 0 24px;
    color: #606266;
    font-size: 16px;
    line-height: 42px;

    &.is-active,
    &:hover {
      color: var(--main-bg-color);
    }
  }

  ::v-deep .el-tabs__active-bar {
    background-color: var(--main-bg-color);
  }

  ::v-deep .el-tabs__nav-wrap::after {
    height: 1px;
    background-color: #ebeef5;
  }
}

.favorites-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 16px;
  padding: 12px 16px;
  border: 1px solid #ebeef5;
  border-radius: 8px;
  background: #fff;

  .favorites-summary {
    color: #606266;
    font-size: 14px;
  }

  .favorites-actions {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 10px;

    .el-button + .el-button {
      margin-left: 0;
    }
  }
}

.goods-collect-button {
  position: absolute;
  top: 8px;
  left: 8px;
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  padding: 0;
  border: none;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.92);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.16);
  color: #909399;
  cursor: pointer;
  font-size: 20px;
  opacity: 0;
  transform: translateY(-4px);
  transition: color 0.2s, opacity 0.2s, transform 0.2s;

  &:hover,
  &.active {
    color: #f5a623;
  }

  &.active {
    opacity: 1;
    transform: translateY(0);
  }

  &:disabled {
    cursor: wait;
  }
}

.goods-select-checkbox {
  position: absolute;
  top: 8px;
  right: 8px;
  z-index: 3;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.94);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.16);

  ::v-deep .el-checkbox__label {
    display: none;
  }
}

.goods-image:hover .goods-collect-button,
.goods-collect-button:focus {
  opacity: 1;
  transform: translateY(0);
}

.searchable-text {
  font: inherit;

  &:hover {
    color: var(--main-bg-color);
  }
}

.card-container {
  margin-bottom: 20px;
  border-radius: 8px;
  border: 1px solid #ebeef5;

  ::v-deep .el-card__body {
    padding: 20px;
  }
}

.filter-section {
  .filter-group {
    display: flex;
    align-items: flex-start;
    margin-bottom: 15px;
    &:last-child {
      margin-bottom: 0;
    }

    .filter-label {
      font-weight: 500;
      margin-right: 15px;
      min-width: 80px;
      color: #606266;
    }

    .brand-list {
      display: flex;
      flex-wrap: wrap;
      gap: 10px;
      min-width: 0;
      transition: max-height 0.25s ease;
    }

    .brand-filter-content {
      width: 100%;
      min-width: 0;
    }

    .brand-list--collapsed {
      max-height: 234px;
      overflow: hidden;
    }

    .brand-toggle-row {
      display: flex;
      justify-content: center;
      margin-top: 4px;

      .el-button {
        padding: 6px 12px 0;

        i {
          margin-left: 4px;
        }
      }
    }

    .mobile-brand-more {
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 6px;
      width: 100%;
      margin-top: 8px;
      padding: 8px 12px;
      border: 0;
      border-radius: 8px;
      background: #f7f8fa;
      color: #606266;
      font-size: 12px;
      cursor: pointer;
    }

    .brand-item {
      position: relative;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      width: 100px;
      height: 112px;
      padding: 6px;
      border: 1px solid #f2e9e7;
      border-radius: 10px;
      background: linear-gradient(180deg, #fff 0%, #fff8f6 100%);
      box-shadow: 0 4px 12px rgba(103, 63, 53, 0.07);
      color: #606266;
      cursor: pointer;
      box-sizing: border-box;
      transition: border-color 0.2s, color 0.2s, box-shadow 0.2s;

      &:hover,
      &.active {
        border-color: var(--main-bg-color);
        color: var(--main-bg-color);
      }

      &.active {
        box-shadow: 0 0 0 1px var(--main-bg-color) inset;
      }
    }

    .brand-item--all {
      height: 112px;
      font-size: 13px;
    }

    .brand-favorite {
      position: absolute;
      top: 5px;
      right: 5px;
      z-index: 2;
      display: flex;
      align-items: center;
      justify-content: center;
      width: 24px;
      height: 24px;
      border-radius: 50%;
      background: rgba(255, 255, 255, 0.92);
      color: #c0c4cc;
      font-size: 17px;
      box-shadow: 0 1px 5px rgba(0, 0, 0, 0.12);
      transition: color 0.2s, transform 0.2s;

      &:hover {
        color: #f5a623;
        transform: scale(1.08);
      }

      &.active {
        color: #f5a623;
      }
    }

    .brand-logo {
      width: 72px;
      height: 72px;
      flex: 0 0 72px;
      border-radius: 6px;
      background: #f5f7fa;
    }

    .brand-logo-error {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 100%;
      height: 100%;
      color: #c0c4cc;
      font-size: 20px;
    }

    .brand-name {
      display: block;
      width: 100%;
      margin-top: 5px;
      overflow: hidden;
      font-size: 14px;
      line-height: 1.5;
      text-align: center;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    .sort-buttons {
      display: flex;
      gap: 10px;
      align-items: center;
    }

    .sort-item {
      display: flex;
      align-items: center;
      gap: 5px;
      margin: 6px 12px;
      //   border: 1px solid #dcdfe6;
      //   border-radius: 4px;
      cursor: pointer;
      transition: all 0.3s;
      //   background-color: #fff;

      &:hover {
        // border-color: var(--main-bg-color);
        color: var(--main-bg-color);
      }

      &.active {
        // border-color: var(--main-bg-color);
        // background-color: #ecf5ff;
        color: var(--main-bg-color);
      }

      .sort-label {
        font-size: 14px;
        user-select: none;
      }

      .sort-arrows {
        display: flex;
        flex-direction: column;
        margin-left: 4px;

        i {
          font-size: 11px;
          line-height: 1;
          color: #909399;
          transition: color 0.3s;
          cursor: pointer;

          &:hover {
            color: var(--main-bg-color);
          }

          &.active {
            color: var(--main-bg-color);
            font-weight: bold;
          }
        }
      }
    }

    ::v-deep {
      .el-radio-group {
        display: flex;
        flex-wrap: wrap;
        gap: 10px;
        max-width: 100%;
      }

      .el-radio-button__inner {
        border-radius: 6px !important;
        border: 1px solid #dcdfe6 !important;
        margin: 0 !important;
        transition: all 0.3s;
      }

      .el-radio-button__orig-radio:checked + .el-radio-button__inner {
        box-shadow: none !important;
        border-color: var(--main-bg-color) !important;
      }

      .el-radio-button:first-child .el-radio-button__inner,
      .el-radio-button:last-child .el-radio-button__inner {
        border-radius: 6px !important;
      }
    }
  }
}

.goods-list {
  .empty-data {
    padding: 40px 0;
    text-align: center;
    color: #909399;
    font-size: 14px;
  }

  // 高佣库 keyword 搜索结果样式（已合并到主列表，仅保留标题样式可复用时使用）
  .hc-search-title {
    margin: 0 0 10px 0;
    font-size: 16px;
    font-weight: 600;
    color: #303133;
  }

  .no-cps-tip {
    padding: 20px;
    text-align: center;
    background: #fff3cd;
    border: 1px solid #ffc107;
    border-radius: 4px;
    margin-bottom: 20px;

    p {
      margin: 0;
      color: #856404;
      font-size: 14px;
      font-weight: 500;
    }
  }

  .recommend-title {
    margin-bottom: 20px;
    padding: 15px 20px;
    background: #f0f9ff;
    border-left: 4px solid var(--main-bg-color);
    border-radius: 4px;

    h3 {
      margin: 0;
      font-size: 18px;
      font-weight: 600;
      color: #303133;
    }
  }

  .goods-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
    gap: 15px;
    width: 100%;
    min-width: 0;
    margin: 0 auto;
  }

  .goods-item {
    min-width: 0;
    max-width: 100%;
    margin-bottom: 15px;
  }

  .goods-card {
    position: relative;
    background-color: #fff;
    border-radius: 8px;
    box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
    transition: all 0.3s ease;
    height: 100%;
    min-width: 0;
    max-width: 100%;
    overflow: hidden;
    display: flex;
    flex-direction: column;
    min-height: 360px;

    &:hover {
      transform: translateY(-5px);
      box-shadow: 0 5px 15px rgba(0, 0, 0, 0.15);
    }
  }

  .goods-image {
    width: 100%;
    min-width: 0;
    max-width: 100%;
    height: 0;
    padding-bottom: 100%;
    /* 1:1 比例 */
    position: relative;
    overflow: hidden;
    background-color: #f9f9f9;
    border-bottom: 1px solid #f0f0f0;

    ::v-deep .el-image {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      display: flex;
      justify-content: center;
      align-items: center;

      img {
        max-width: 100%;
        max-height: 100%;
        object-fit: contain;
        border-radius: 8px 8px 0 0;
        transition: transform 0.3s ease;

        &:hover {
          transform: scale(1.05);
        }
      }
    }

    .image-slot {
      display: flex;
      justify-content: center;
      align-items: center;
      width: 100%;
      height: 100%;
      position: absolute;
      top: 0;
      left: 0;
      background: #f5f7fa;
      color: #909399;
      font-size: 24px;
    }
  }

  .goods-content {
    padding: 10px;
    flex: 1;
    min-width: 0;
    max-width: 100%;
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
  }

  .goods-title {
    min-width: 0;
    max-width: 100%;
    font-size: 14px;
    color: #222222;
    line-height: 1.4;
    margin-bottom: 5px;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
    overflow-wrap: anywhere;
    min-height: 36px;
    cursor: pointer;

    .platform-tag {
      font-weight: normal;
      padding: 0 4px;
      border-radius: 2px;
      margin-right: 3px;
      vertical-align: middle;
      font-size: 13px;
    }

    .cpm-tag {
      margin-left: 5px;
      font-size: 12px;
      padding: 0 6px;
    }
  }
  .flagship-icon {
    display: inline-block;
    width: 66px;
    // height: 14px;
    vertical-align: middle;
    margin-right: 2px;
  }

  .commission-rate-comparison {
    margin-bottom: 8px;
    padding: 8px;
    background: #f5f7fa;
    border-radius: 4px;
    border: 1px solid #e4e7ed;

    .rate-item {
      display: flex;
      align-items: center;
      margin-bottom: 6px;
      font-size: 13px;

      &:last-child {
        margin-bottom: 0;
      }

      &.highlight {
        .rate-value {
          color: #f56c6c;
          font-weight: bold;
          font-size: 15px;
        }

        .rate-tip {
          margin-left: 8px;
          color: #f56c6c;
          font-weight: bold;
          font-size: 12px;
        }
      }

      .rate-label {
        color: #606266;
        margin-right: 5px;
      }

      .rate-value {
        color: #303133;
        font-weight: 500;
      }
    }
  }

  .price-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 10px;
  }

  .price-info {
    line-height: 1;
    .current-price {
      color: #e2231a;
      font-weight: bold;

      .price-number {
        font-size: 26px;
      }
    }

    .original-price {
      color: #999;
      font-size: 13px;
      text-decoration: line-through;
      margin-top: 2px;
    }
  }

  .goods-tags {
    text-align: right;
    line-height: 1.2;
    .price-tag {
      font-size: 14px;
      margin-left: 3px;
      margin-bottom: 3px;
      font-weight: bold;
      transform-origin: right center;
    }
    .commission-rate {
      color: #ff3b52;
      font-size: 14px;
      margin-bottom: 3px;
      font-weight: bold;
      transform-origin: right center;
    }
    .commission-text {
      // color: #ff9012;
      color: #ff3b52;
      font-size: 14px;
      transform-origin: right center;
    }
  }
  .sales-info-container {
    display: flex;
    width: 100%;
    min-width: 0;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 12px;
    .shop-title {
      display: block;
      flex: 1;
      min-width: 0;
      max-width: 100%;
      padding: 0;
      overflow: hidden;
      border: none;
      background: transparent;
      font-size: 13px;
      color: #666666;
      cursor: pointer;
      text-align: left;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
    .sales-info {
      display: flex;
      flex: 0 0 auto;
      align-items: center;
      .sales-text {
        font-size: 12px;
        color: #909399;
      }
    }
  }
  .brand-info-row {
    display: flex;
    align-items: center;
    gap: 6px;
    min-width: 0;
    margin: -5px 0 10px;
    font-size: 12px;

    .brand-info-label {
      flex: 0 0 auto;
      padding: 1px 5px;
      border-radius: 3px;
      background: #f5f5f5;
      color: #222222;
    }

    .brand-info-value {
      min-width: 0;
      padding: 0;
      overflow: hidden;
      border: none;
      background: #222222;
      color: #ffffff;
      cursor: pointer;
      border-radius: 4px;
      padding: 1px 6px;
      font-size: 13px;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
  }
  .sales-info {
    display: flex;
    align-items: center;
    .sales-text {
      font-size: 12px;
      color: #909399;
    }
  }
  .meta-bar {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    background: #fafafa;
    border-radius: 8px;
    padding: 12px 4px;
    .meta-item {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 4px;
      text-align: center;
      border-right: 1px solid #e2e2e2;

      &:last-child {
        border-right: none;
      }
    }
    .meta-item--primary .meta-value {
      color: #e2231a;
      font-size: 18px;
    }

    .meta-label {
      font-size: 12px;
      color: #94a3b8;
      line-height: 1;
    }

    .meta-value {
      font-size: 20px;
      color: #475569;
      font-weight: 700;
      line-height: 1.2;
    }
  }
  
  
  .action-row {
    display: flex;
    flex-direction: column;
    justify-content: flex-end;
    align-items: stretch;
    gap: 8px;
    margin-top: auto;
    padding-top: 10px;
    // border-top: 1px solid #f0f0f0;

    ::v-deep .el-button {
      width: 100%;
      margin: 0;
      padding: 10px;
      font-size: 14px;
      color: var(--main-bg-color) !important;
      border: 1px solid var(--main-bg-color) !important;
      background: transparent !important;
      &:hover {
        background: var(--main-bg-color) !important;
        color: #ffffff !important;
      }
    }

    ::v-deep .selection-cart-add {
      border-style: dashed !important;
      background: #fff !important;

      &:hover:not(.is-disabled) {
        background: #f4faff !important;
        color: var(--main-bg-color) !important;
      }

      &.is-disabled {
        border-color: #dcdfe6 !important;
        color: #909399 !important;
      }

      &.selection-cart-add--added.is-disabled {
        border-style: solid !important;
        border-color: #67c23a !important;
        background: #f0f9eb !important;
        color: #3b9f2f !important;
        opacity: 1;
      }
    }
    .goods-id {
      .id-text {
        font-size: 12px;
        color: #919191;
        cursor: pointer;
        display: inline-block;

        &:hover {
          text-decoration: underline;
        }

        i {
          margin-right: 3px;
        }
      }
    }
  }
}

.selection-cart-entry {
  position: fixed;
  right: 24px;
  bottom: 72px;
  z-index: 1000;

  ::v-deep .el-badge__content {
    border: 2px solid #fff;
    background: #f56c6c;
    font-weight: 600;
  }
}

.selection-cart-float-button {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  gap: 3px;
  width: 64px;
  height: 64px;
  padding: 0;
  border: none;
  border-radius: 50%;
  background: var(--main-bg-color);
  box-shadow: 0 5px 18px rgba(44, 117, 163, 0.3);
  color: #fff;
  cursor: pointer;
  transition: transform 0.2s, box-shadow 0.2s;

  i {
    font-size: 22px;
  }

  span {
    font-size: 11px;
  }

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 8px 22px rgba(44, 117, 163, 0.38);
  }
}

.selection-cart-entry--bounce .selection-cart-float-button {
  animation: selection-cart-bounce 0.42s ease;
}

.mobile-brand-drawer {
  ::v-deep .el-drawer {
    border-radius: 16px 16px 0 0;
    overflow: hidden;
  }

  ::v-deep .el-drawer__header {
    flex: 0 0 auto;
    margin-bottom: 0;
    padding: 18px 16px 14px;
    border-bottom: 1px solid #ebeef5;
    color: #303133;
    font-weight: 600;
  }

  ::v-deep .el-drawer__body {
    min-height: 0;
  }
}

.mobile-brand-panel {
  display: flex;
  flex-direction: column;
  height: 100%;
  padding: 12px 12px max(12px, env(safe-area-inset-bottom));
  box-sizing: border-box;
}

.mobile-brand-search {
  flex: 0 0 auto;
}

.mobile-brand-summary {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex: 0 0 auto;
  gap: 12px;
  padding: 10px 2px;
  color: #909399;
  font-size: 12px;
}

.mobile-brand-current {
  overflow: hidden;
  color: var(--main-bg-color);
  text-overflow: ellipsis;
  white-space: nowrap;
}

.mobile-brand-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 8px;
  min-height: 0;
  padding: 1px 1px 8px;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
}

.mobile-brand-card {
  position: relative;
  display: flex;
  align-items: center;
  flex-direction: column;
  justify-content: center;
  min-width: 0;
  height: 82px;
  padding: 7px 5px 5px;
  border: 1px solid #ebeef5;
  border-radius: 9px;
  background: #fff;
  color: #606266;
  box-sizing: border-box;

  &.active {
    border-color: var(--main-bg-color);
    background: #fff7f5;
    color: var(--main-bg-color);
    box-shadow: 0 0 0 1px var(--main-bg-color) inset;
  }
}

.mobile-brand-card-logo,
.mobile-brand-all-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 44px;
  width: 44px;
  height: 44px;
  border-radius: 6px;
  background: #f5f7fa;
}

.mobile-brand-all-icon {
  color: var(--main-bg-color);
  font-size: 21px;
}

.mobile-brand-card-name {
  display: block;
  width: 100%;
  margin-top: 4px;
  overflow: hidden;
  font-size: 11px;
  line-height: 15px;
  text-align: center;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.mobile-brand-favorite {
  position: absolute;
  top: 3px;
  right: 3px;
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.94);
  color: #c0c4cc;
  font-size: 15px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.1);

  &.active {
    color: #f5a623;
  }
}

.mobile-brand-empty {
  padding: 48px 0;
  color: #909399;
  font-size: 13px;
  text-align: center;
}

@keyframes selection-cart-bounce {
  0%, 100% { transform: scale(1); }
  45% { transform: scale(1.18); }
  70% { transform: scale(0.94); }
}

.selection-cart-panel {
  display: flex;
  flex-direction: column;
  height: calc(100vh - 78px);
  padding: 0 20px 20px;
  box-sizing: border-box;
}

.selection-cart-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-bottom: 12px;
  border-bottom: 1px solid #ebeef5;
  color: #606266;
  font-size: 14px;
}

.selection-cart-header-main {
  display: flex;
  align-items: center;
  gap: 18px;
}

.selection-cart-clear {
  color: #f56c6c;
}

.selection-cart-list {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
}

.selection-cart-item {
  position: relative;
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 16px 32px 16px 0;
  border-bottom: 1px solid #f0f0f0;
}

.selection-cart-checkbox {
  flex: 0 0 auto;
  margin-top: 31px;

  ::v-deep .el-checkbox__label {
    display: none;
  }
}

.selection-cart-image {
  flex: 0 0 82px;
  width: 82px;
  height: 82px;
  border-radius: 7px;
  background: #f5f7fa;
}

.selection-cart-image-error,
.selection-cart-empty {
  display: flex;
  align-items: center;
  justify-content: center;
}

.selection-cart-image-error {
  width: 100%;
  height: 100%;
  color: #c0c4cc;
}

.selection-cart-info {
  flex: 1;
  min-width: 0;
}

.selection-cart-title {
  display: -webkit-box;
  overflow: hidden;
  color: #303133;
  font-size: 14px;
  line-height: 1.45;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
}

.selection-cart-author {
  margin-top: 7px;
  overflow: hidden;
  color: #909399;
  font-size: 12px;
  text-overflow: ellipsis;
  white-space: nowrap;

  i {
    margin-right: 4px;
  }
}

.selection-cart-income {
  display: flex;
  gap: 14px;
  margin-top: 9px;
  color: #909399;
  font-size: 12px;

  strong {
    color: #e2231a;
    font-size: 14px;
  }
}

.selection-cart-remove {
  position: absolute;
  top: 16px;
  right: 2px;
  padding: 4px;
  border: none;
  background: transparent;
  color: #c0c4cc;
  cursor: pointer;
  font-size: 17px;

  &:hover {
    color: #f56c6c;
  }
}

.selection-cart-footer {
  display: grid;
  flex: 0 0 auto;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;
  padding-top: 14px;
  border-top: 1px solid #ebeef5;

  .el-button {
    width: 100%;
    margin: 0;
  }
}

.batch-promote-result {
  min-height: 300px;

  ::v-deep .el-textarea__inner {
    line-height: 1.7;
    resize: none;
  }
}

.batch-promote-options {
  margin-top: 16px;
  overflow: hidden;
  border: 1px solid #ebeef5;
  border-radius: 8px;
  background: #fafafa;
}

.batch-promote-option {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
  padding: 12px 14px;

  & + & {
    border-top: 1px solid #ebeef5;
  }
}

.batch-promote-option__title,
.batch-promote-option__tip {
  display: block;
}

.batch-promote-option__title {
  color: #303133;
  font-size: 14px;
  line-height: 20px;
}

.batch-promote-option__tip {
  margin-top: 2px;
  color: #909399;
  font-size: 12px;
  line-height: 18px;
}

.selection-cart-empty {
  flex: 1;
  flex-direction: column;
  gap: 10px;
  color: #c0c4cc;

  i {
    font-size: 42px;
  }
}

.load-more {
  text-align: center;
  padding: 20px 0;

  p {
    margin: 0;
    font-size: 14px;
    color: #909399;
  }

  .loading-text {
    color: var(--main-bg-color);

    i {
      margin-right: 5px;
    }
  }

  .no-more-text {
    color: #909399;
  }

  .load-more-text {
    color: #909399;
  }
}

// 响应式设计
@media (max-width: 1400px) {
  .goods-list {
    .goods-grid {
      grid-template-columns: repeat(auto-fill, minmax(170px, 1fr));
    }
  }
}

@media (max-width: 1200px) {
  .goods-list {
    .goods-grid {
      grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
      max-width: 1000px;
    }

    .goods-card {
      min-height: 340px;
    }
  }
}

@media (max-width: 992px) {
  .goods-list {
    .goods-grid {
      grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
      max-width: 800px;
    }

    .goods-card {
      min-height: 330px;
    }

    .goods-title {
      min-height: 38px;
      -webkit-line-clamp: 2;
      font-size: 13px;
    }
  }
}

@media (max-width: 768px) {
  .dygaoyong-page {
    padding: 10px;
  }

  .library-tabs {
    margin-bottom: 10px;
  }

  .favorites-toolbar {
    align-items: flex-start;
    flex-direction: column;
    padding: 10px 12px;
  }

  .selection-cart-entry {
    right: 14px;
    bottom: 24px;
  }

  .selection-cart-float-button {
    width: 56px;
    height: 56px;

    i {
      font-size: 20px;
    }
  }

  .selection-cart-panel {
    padding: 0 14px 14px;
  }

  .selection-cart-item {
    gap: 9px;
  }

  .selection-cart-image {
    flex-basis: 68px;
    width: 68px;
    height: 68px;
  }

  .selection-cart-income {
    flex-direction: column;
    gap: 3px;
  }

  .page-header {
    flex-direction: column;
    align-items: flex-start;
    margin: 12px 0 14px;
    width: 100%;

    .header-search {
      width: 100%;
      max-width: none;

      .search-box,
      .search-row {
        width: 100%;
      }

      .search-row {
        height: 40px;
      }

      .search-type-select {
        flex-basis: 88px;
        width: 88px;

        ::v-deep .el-input__inner {
          height: 38px;
          padding-left: 9px;
          padding-right: 24px;
          font-size: 12px;
        }
      }

      .search-input-large {
        flex: 1 1 auto;
        min-width: 0;

        ::v-deep .el-input__inner {
          height: 38px;
          padding: 0 10px;
          font-size: 13px;
        }
      }

      .search-button {
        flex-basis: 52px;
        width: 52px;
        height: 40px;
        padding: 0;
        font-size: 0;

        ::v-deep i {
          margin-right: 0;
          font-size: 15px;
        }

        ::v-deep span {
          display: none;
        }
      }
    }
  }

  .card-container {
    margin-bottom: 12px;

    ::v-deep .el-card__body {
      padding: 12px;
    }
  }

  .filter-result-bar {
    align-items: flex-start;
    margin-bottom: 12px;
    padding: 10px 12px;

    .filter-result-count {
      width: 100%;
    }
  }

.filter-group {
      flex-direction: column;
      align-items: flex-start;
      width: 100%;

      .filter-label {
        margin-bottom: 10px;
        line-height: normal;
      }

      &.brand-filter-group {
        .brand-list {
          display: grid;
          grid-template-columns: repeat(4, minmax(0, 1fr));
          gap: 6px;
          width: 100%;
        }

        .brand-item,
        .brand-item--all {
          width: auto;
          height: 58px;
          min-width: 0;
          padding: 4px 3px;
          border-radius: 8px;
          box-shadow: none;
        }

        .brand-logo {
          flex: 0 0 30px;
          width: 30px;
          height: 30px;
          border-radius: 4px;
        }

        .brand-name {
          margin-top: 2px;
          font-size: 11px;
          line-height: 14px;
        }

        .brand-favorite {
          top: 1px;
          right: 1px;
          width: 18px;
          height: 18px;
          font-size: 13px;
          box-shadow: none;
        }
      }

      &:first-child {
        ::v-deep .el-radio-group {
          display: flex;
          flex-wrap: nowrap;
          gap: 0;
          width: 100%;
          overflow-x: auto;
          overflow-y: hidden;
          padding-bottom: 4px;
          white-space: nowrap;
          -webkit-overflow-scrolling: touch;
        }

        ::v-deep .el-radio-button {
          flex: 0 0 auto;
        }

        ::v-deep .el-radio-button__inner {
          margin-right: 8px !important;
        }
      }

      .sort-buttons {
        width: 100%;
        overflow-x: auto;
        padding-bottom: 4px;
        gap: 2px;
        -webkit-overflow-scrolling: touch;
      }

      .sort-item {
        flex: 0 0 auto;
        margin: 6px 10px 6px 0;
      }
    }

  .goods-list {
    .goods-collect-button {
      opacity: 1;
      transform: translateY(0);
    }

    .goods-grid {
      grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
      gap: 10px;
    }

    .goods-card {
      min-height: 320px;
    }

    .goods-content {
      padding: 8px;
    }

    .goods-title {
      font-size: 12px;
      margin-bottom: 6px;
      min-height: 34px;
    }

    .price-info {
      .current-price .price-number {
        font-size: 15px;
      }
    }

    .action-row {
      flex-direction: column;
      align-items: flex-start;

      .goods-id {
        margin-bottom: 6px;
      }

      .el-button {
        width: 100%;
        padding: 5px 10px;
        font-size: 12px;
      }
    }
  }

  .promote-dialog {
    ::v-deep .el-dialog {
      max-width: calc(100vw - 24px);
      margin-top: 8vh !important;
    }

    ::v-deep .el-dialog__body {
      padding: 14px;
    }

    ::v-deep .el-dialog__footer {
      padding: 10px 14px 14px;
    }
  }

  .promote-result {
    max-height: 68vh;
    min-height: 160px;
    overflow-y: auto;

    .promote-fields {
      .promote-field {
        margin-bottom: 14px;

        .field-value {
          flex-direction: column;
          gap: 8px;

          .field-input,
          .el-button {
            width: 100%;
          }
        }
      }
    }
  }
}

@media (max-width: 576px) {
  .goods-list {
    .goods-grid {
      grid-template-columns: repeat(2, minmax(0, 1fr));
      gap: 8px;
    }

    .goods-card {
      min-height: 300px;
    }

    .goods-title {
      font-size: 12px;
    }
  }
}

// 推广弹窗样式
.promote-dialog {
  ::v-deep .el-dialog__body {
    padding: 20px;
  }
}

.promote-result {
  min-height: 200px;

  .promote-fields {
    .promote-field {
      margin-bottom: 20px;

      &:last-child {
        margin-bottom: 0;
      }

      label {
        display: block;
        margin-bottom: 8px;
        font-weight: 500;
        color: #606266;
        font-size: 14px;
      }

      .field-value {
        display: flex;
        gap: 10px;
        align-items: flex-start;

        .field-input {
          flex: 1;
          min-width: 0;

          ::v-deep .el-textarea__inner {
            font-family: "Courier New", monospace;
            font-size: 12px;
            line-height: 1.5;
          }
        }

        .promote-copy {
          ::v-deep .el-textarea__inner {
            font-family: inherit;
            color: #303133;
          }
        }

        .el-button {
          white-space: nowrap;
        }
      }

      .single-promote-option {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 20px;
        margin-top: 12px;
        padding: 10px 12px;
        border: 1px solid #ebeef5;
        border-radius: 7px;
        background: #fafafa;
      }

      .single-promote-option__title,
      .single-promote-option__tip {
        display: block;
      }

      .single-promote-option__title {
        color: #303133;
        font-size: 14px;
        line-height: 20px;
      }

      .single-promote-option__tip {
        margin-top: 2px;
        color: #909399;
        font-size: 12px;
        line-height: 18px;
      }
    }
  }

  .deeplink-button {
    width: 100%;
  }
}
/* 针对 single-goods-card 的特殊布局 */
.single-goods-card {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr)) !important;
}
.commission-pk-container {
  display: flex;
  align-items: stretch; // 让左右高度一致
  background: #fff;
  border-radius: 8px;
  overflow: hidden;
  border: 1px solid #ebeef5;
  margin-bottom: 10px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);

  .pk-item {
    flex: 1;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    padding: 10px 4px;
    position: relative;

    &.loser {
      background: #f5f7fa;
      color: #333333;

      .label {
        font-size: 12px;
        margin-bottom: 2px;
      }
      .value {
        font-size: 14px;
        font-weight: 500;
      }
    }

    &.winner {
      background: #fff1f0; // 浅红色背景烘托
      color: #f12727;
      flex: 1.3; // 让赢家占据更大空间

      .badge {
        position: absolute;
        top: 0;
        right: 0;
        background: #f12727;
        color: #fff;
        font-size: 10px;
        padding: 1px 6px;
        border-bottom-left-radius: 6px;
      }

      .label {
        font-size: 14px;
        opacity: 0.8;
        margin-bottom: 2px;
      }

      .value-group {
        display: flex;
        align-items: baseline;

        .num {
          font-size: 20px; // 放大数字
          font-weight: bold;
          line-height: 1;
        }

        .up-tag {
          font-size: 14px;
          background: #e23232;
          color: white;
          padding: 1px 4px;
          border-radius: 4px;
          margin-left: 4px;
          transform: scale(0.9);
        }
      }
    }
  }

  .pk-divider {
    width: 0;
    position: relative;
    z-index: 1;

    .vs-tag {
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      background: #fff;
      border: 1px solid #e4e7ed;
      color: #c0c4cc;
      font-size: 10px;
      padding: 2px;
      border-radius: 50%;
      font-weight: bold;
      width: 20px;
      height: 20px;
      display: flex;
      align-items: center;
      justify-content: center;
    }
  }
}
</style>
